// src/contracts/schema/http.ts
import { z } from "zod/v4";
var coerceToContractAbsoluteBase = (base) => {
  const validatedUrl = new URL(base);
  const href = validatedUrl.href;
  if (href.endsWith("/")) {
    return href;
  } else {
    return `${href}/`;
  }
};
var normalizeSchemaValue = (value) => {
  if (value !== void 0) {
    return value;
  }
  return z.never();
};
var createContractRequest = (parts) => {
  return {
    pathParams: normalizeSchemaValue(parts?.pathParams),
    queryParams: normalizeSchemaValue(parts?.queryParams),
    body: normalizeSchemaValue(parts?.body)
  };
};
var createContractResponse = (parts) => {
  return { body: normalizeSchemaValue(parts?.body) };
};
var createContract = (args) => {
  const request = createContractRequest({
    pathParams: args.request?.pathParams,
    queryParams: args.request?.queryParams,
    body: args.request?.body
  });
  const response = createContractResponse({
    body: args.response?.body
  });
  return {
    method: args.method,
    path: args.path,
    request,
    response
  };
};
var getContractUrl = (params) => {
  const { path } = params.contract;
  const onlyPath = path.split("?")[0];
  const onlyQueryTemplate = path.split("?")[1];
  const getReplacedPath = () => {
    if (params.pathParams && typeof params.pathParams === "object") {
      return Object.entries(params.pathParams).reduce((acc, [key, value]) => {
        const regex = new RegExp(`:${key}(?=/|$)`, "g");
        return acc.replace(regex, encodeURIComponent(String(value)));
      }, onlyPath);
    }
    return onlyPath;
  };
  const getQueryParts = () => {
    const hasTemplate = typeof onlyQueryTemplate === "string";
    if (params.queryParams && typeof params.queryParams === "object") {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params.queryParams)) {
        if (value === void 0) continue;
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.set(key, String(value));
        }
      }
      const queryString2 = searchParams.toString();
      return { queryString: queryString2, includeQuestion: queryString2.length > 0 || hasTemplate };
    }
    if (hasTemplate) {
      return { queryString: `${onlyQueryTemplate}`, includeQuestion: true };
    }
    return { queryString: "", includeQuestion: false };
  };
  const { queryString, includeQuestion } = getQueryParts();
  if (typeof URL === "function") {
    const url = new URL(`.${getReplacedPath()}${includeQuestion ? `?${queryString}` : ""}`, params.base);
    return url.toString();
  } else {
    const baseWithoutTrailingSlash = params.base.endsWith("/") ? params.base.slice(0, -1) : params.base;
    return `${baseWithoutTrailingSlash}${getReplacedPath()}${includeQuestion ? `?${queryString}` : ""}`;
  }
};
var withContractBase = (baseParams) => {
  return {
    withFetchParams: (urlParams) => {
      const url = getContractUrl({
        contract: baseParams.contract,
        base: baseParams.base,
        pathParams: urlParams.pathParams,
        queryParams: urlParams.queryParams
      });
      const { method, response } = baseParams.contract;
      const defaultFetcher = async (args) => {
        const response2 = await fetch(args.url, {
          method: args.method,
          body: args.body ? JSON.stringify(args.body) : void 0
        });
        return response2.json();
      };
      const dataHandlers = (data) => {
        return {
          toRaw: () => data,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          toParsed: async () => response.body.parse(await data)
        };
      };
      return {
        withFetcher: ((fetcher) => {
          const effectiveFetcher = fetcher ?? defaultFetcher;
          return dataHandlers(effectiveFetcher({ method, url, body: urlParams.body }));
        })
      };
    }
  };
};
function createPaginatedResultsSchema(schema, keyName = "results") {
  return z.object({
    currentPage: z.number(),
    totalElements: z.number(),
    totalPages: z.number(),
    [keyName]: z.array(schema)
  });
}

// src/contracts/data-dump-http.ts
import * as z2 from "zod/v4";
var DataDumpSourceSchema = z2.union([
  z2.literal("machine-learning-face-detection"),
  z2.literal("machine-learning-object-detection"),
  z2.literal("machine-learning-audio-transcription"),
  z2.templateLiteral(["machine-learning-face-detection-v", z2.number().min(1).positive()]),
  z2.templateLiteral(["machine-learning-object-detection-v", z2.number().min(1).positive()])
]);
var DataDumpMimeTypeSchema = z2.union([
  z2.literal("application/json"),
  z2.literal("application/octet-stream"),
  z2.literal("application/xml")
]);
var DataDumpGrantContract = createContract({
  method: "POST",
  path: "/data-dump/:proctoringSessionUuid/grant",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.object({
      source: DataDumpSourceSchema,
      mimeType: DataDumpMimeTypeSchema
    })
  },
  response: {
    body: z2.object({
      grantId: z2.string(),
      uploadUrl: z2.string()
    })
  }
});
var DataDumpCallbackContract = createContract({
  method: "POST",
  path: "/data-dump/:proctoringSessionUuid/callback",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.object({
      grantId: z2.string()
    })
  }
});
var DataDumpLinksContract = createContract({
  method: "GET",
  path: "/data-dump/:proctoringSessionUuid/links",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    })
  },
  response: {
    body: z2.object({
      links: z2.array(
        z2.object({
          source: DataDumpSourceSchema,
          mimeType: DataDumpMimeTypeSchema,
          downloadUrl: z2.string()
        })
      )
    })
  }
});

// src/contracts/logging-http.ts
import * as z3 from "zod/v4";
var MachineLearningEventSchema = z3.object({
  type: z3.literal("machine-learning"),
  time: z3.date()
});
var FaceDetectionStateSchema = z3.union([
  z3.literal("detected-no-face"),
  z3.literal("detected-target-face"),
  z3.literal("detected-unknown-face"),
  z3.literal("detected-many-faces")
]);
var FaceDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("face-detection"),
  details: z3.object({
    previousState: FaceDetectionStateSchema,
    currentState: FaceDetectionStateSchema
  })
});
var TargetObjectsSchema = z3.union([z3.literal("cell-phone"), z3.literal("laptop"), z3.literal("book")]);
var ObjectDetectionStateSchema = z3.union([
  z3.literal("detected-no-object"),
  z3.templateLiteral(["detected-", TargetObjectsSchema], "-object"),
  z3.literal("detected-many-objects")
]);
var ObjectDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("object-detection"),
  details: z3.object({
    previousState: ObjectDetectionStateSchema,
    currentState: ObjectDetectionStateSchema
  })
});
var FlaggedKeywordEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("flagged-keyword"),
  details: z3.object({
    keyword: z3.string()
  })
});
var LogMachineLearningQuizEventContract = createContract({
  method: "POST",
  path: "/quiz/event/:proctoringSessionUuid",
  request: {
    pathParams: z3.object({
      proctoringSessionUuid: z3.string()
    }),
    body: z3.union([FaceDetectionEventSchema, ObjectDetectionEventSchema, FlaggedKeywordEventSchema])
  }
});

// src/contracts/restricted-device-event.ts
import * as z6 from "zod/v4";

// src/contracts/json/index.ts
import { z as z4 } from "zod";
var FlaggedKeywordsSchema = z4.union([
  z4.array(z4.object({ keyword: z4.string(), level: z4.number().optional() })),
  z4.null()
]);
var RestrictedDeviceOptionsSchema = z4.object({
  restrictDevice: z4.boolean(),
  videoRecordingMode: z4.union([z4.literal("off"), z4.literal("normal"), z4.literal("strict")])
});
var RestrictedDeviceStateDataSchema = z4.object({
  isLocked: z4.boolean(),
  recordingStatus: z4.optional(
    z4.object({
      video: z4.optional(
        z4.object({
          selectedCamera: z4.optional(z4.union([z4.literal("front"), z4.literal("back")])),
          isRecording: z4.boolean()
        })
      )
    })
  )
});

// src/contracts/schema/event.ts
import * as z5 from "zod/v4";
var createEventContract = (namespace, handshake, events) => {
  return {
    namespace,
    queryParams: handshake.queryParams,
    events
  };
};
var createDiscriminableEventPayload = (key, schema) => {
  return z5.object({
    key: z5.literal(key),
    ...schema.shape
  });
};
var createDiscriminableEvent = (...args) => {
  return z5.discriminatedUnion("key", args);
};

// src/contracts/restricted-device-event.ts
var RestrictedDeviceEventContract = createEventContract(
  "/restricted-device",
  {
    queryParams: z6.object({
      secret: z6.string(),
      peerId: z6.string()
    })
  },
  {
    message: createDiscriminableEvent(
      createDiscriminableEventPayload("pollRequest", z6.object({})),
      createDiscriminableEventPayload(
        "pollResponse",
        z6.object({
          devices: z6.array(
            z6.object({
              peerId: z6.string(),
              connected: z6.boolean(),
              data: z6.any()
            })
          )
        })
      ),
      createDiscriminableEventPayload(
        "options",
        RestrictedDeviceOptionsSchema.extend({
          proctoringSessionId: z6.optional(z6.string()),
          accountId: z6.optional(z6.string()),
          authCode: z6.optional(z6.string())
        })
      ),
      createDiscriminableEventPayload("stateUpdate", RestrictedDeviceStateDataSchema),
      createDiscriminableEventPayload("error", z6.object({ code: z6.string() }))
    )
  }
);

// src/contracts/restricted-device-http.ts
import * as z8 from "zod/v4";

// src/contracts/restricted-device-common.ts
import * as z7 from "zod";
var ProctoringSessionEventSchema = z7.object({
  id: z7.number().int(),
  proctoringSessionId: z7.number().int(),
  type: z7.string(),
  name: z7.string(),
  details: z7.unknown(),
  time: z7.date(),
  endTime: z7.date().nullish(),
  createdAt: z7.date(),
  updatedAt: z7.date(),
  anomalyRef: z7.number().int().nullish()
});
var RestrictedDeviceRoleSchema = z7.enum(["source", "sink"]);
var RestrictedDevicePlatformSchema = z7.enum(["extension", "mobile", "lockdown", "browser"]);
var RestrictedDevicePeerSchema = z7.object({
  id: z7.string(),
  proctoringSessionId: z7.number().int(),
  role: RestrictedDeviceRoleSchema,
  platform: RestrictedDevicePlatformSchema,
  options: z7.unknown()
});
var RestrictedDeviceStateSchema = z7.object({
  id: z7.number().int(),
  peerId: z7.string(),
  connected: z7.boolean(),
  data: z7.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10) return depth;
      if (obj === null || typeof obj !== "object") return depth;
      const values = Object.values(obj);
      if (values.length === 0) return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z7.date(),
  updatedAt: z7.date()
});
var RestrictedDeviceLoggedEventRootSchema = z7.object({
  ...ProctoringSessionEventSchema.shape,
  type: z7.literal("restricted-device")
});
var SharedEventDetailsSchema = z7.object({
  peer: z7.object({
    ...RestrictedDevicePeerSchema.omit({
      proctoringSessionId: true,
      options: true
    }).shape,
    options: z7.union([RestrictedDeviceOptionsSchema, z7.null()])
  })
});
var RecordingEventDetailsSchema = z7.object({
  ...SharedEventDetailsSchema.shape,
  source: z7.object({ type: z7.literal("video") })
});
var RestrictedDeviceLoggedEventSchema = z7.discriminatedUnion("name", [
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("state"),
    details: z7.object({
      ...SharedEventDetailsSchema.shape,
      state: z7.object({
        ...RestrictedDeviceStateSchema.pick({
          connected: true
        }).shape,
        data: z7.union([RestrictedDeviceStateDataSchema, z7.null()])
      })
    })
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("connect"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("disconnect"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("lock"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("unlock"),
    details: SharedEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("start recording"),
    details: RecordingEventDetailsSchema
  }),
  z7.object({
    ...RestrictedDeviceLoggedEventRootSchema.shape,
    name: z7.literal("end recording"),
    details: RecordingEventDetailsSchema
  })
]);

// src/contracts/restricted-device-http.ts
var RestrictedDeviceRegistrationContract = createContract({
  method: "POST",
  path: "/restricted-device/register",
  request: {
    body: z8.object({
      role: RestrictedDeviceRoleSchema,
      platform: RestrictedDevicePlatformSchema,
      proctoringSessionUuid: z8.string(),
      options: z8.optional(RestrictedDeviceOptionsSchema)
    })
  },
  response: {
    body: z8.object({
      peerId: z8.string()
    })
  }
});
var RestrictedDeviceDeletePeerContract = createContract({
  method: "DELETE",
  path: "/restricted-device/:restrictedDevicePeerId",
  request: {
    pathParams: z8.object({
      restrictedDevicePeerId: z8.string()
    })
  }
});
var RestrictedDeviceGrantContract = createContract({
  method: "POST",
  path: "/restricted-device/:restrictedDevicePeerId/grant",
  request: {
    pathParams: z8.object({
      restrictedDevicePeerId: z8.string()
    })
  },
  response: {
    body: z8.object({
      secret: z8.string()
    })
  }
});

// src/contracts/inference-http.ts
import * as z9 from "zod/v4";
var CreateInferenceTokenContract = createContract({
  method: "POST",
  path: "/inference/token",
  request: {
    body: z9.object({
      proctoringSessionUuid: z9.string()
    })
  },
  response: {
    body: z9.object({
      token: z9.string()
    })
  }
});

// src/contracts/proctoring-message/interceptor.ts
import { z as z11 } from "zod";

// src/contracts/proctoring-message/shared.ts
import { z as z10 } from "zod";
var MOBILE_MEDIA_DEVICE_ID = "mobile-f0bfbfc0-bb46-46d4-b083-f0403a23c6e6";
var RecordingModeSchema = z10.union([
  z10.literal("normal"),
  z10.literal("strict"),
  z10.literal("off")
]);
var EnabledRecordingModeSchema = z10.union([
  z10.literal("normal"),
  z10.literal("strict")
]);
var SerializableMediaDeviceInfoSchema = z10.object({
  deviceId: z10.string(),
  groupId: z10.string(),
  kind: z10.union([z10.literal("audioinput"), z10.literal("audiooutput"), z10.literal("videoinput")]),
  label: z10.string()
});
var BaseMediaTrackSettingsSchema = z10.object({
  deviceId: z10.string(),
  groupId: z10.string(),
  width: z10.number(),
  height: z10.number(),
  aspectRatio: z10.number()
}).partial();

// src/contracts/proctoring-message/interceptor.ts
var BrowserTypeSchema = z11.union([
  z11.literal("desktop-browser"),
  z11.literal("desktop-lockdown"),
  z11.literal("mobile-browser"),
  z11.literal("mobile-lockdown"),
  z11.literal("chromeos-browser")
]);
var BrowserArchSchema = z11.union([z11.literal("chromium"), z11.literal("firefox"), z11.literal("safari")]);
var ProctoringPlatformSchema = z11.union([
  z11.literal("extension"),
  z11.literal("lockdown"),
  z11.literal("mobile"),
  z11.literal("browser"),
  z11.literal("none")
]);
var PlatformInfoSchema = z11.object({
  platform: ProctoringPlatformSchema,
  version: z11.nullish(z11.string()),
  versionSupported: z11.boolean()
});
var LmsDetailsSchema = z11.discriminatedUnion("lms", [
  z11.object({
    lms: z11.literal("canvas"),
    assessmentType: z11.optional(z11.union([z11.literal("classicQuiz"), z11.literal("newQuiz"), z11.literal("assignment")]))
  }),
  z11.object({
    lms: z11.literal("BlackboardLearn"),
    courseType: z11.optional(z11.union([z11.literal("original"), z11.literal("ultra")]))
  }),
  z11.object({
    lms: z11.literal("moodle"),
    specialHostname: z11.optional(z11.string()),
    version: z11.optional(z11.string())
  }),
  z11.object({ lms: z11.literal("desire2learn") }),
  z11.object({
    lms: z11.optional(z11.undefined()),
    thirdPartyPlatform: z11.optional(z11.string()),
    thirdPartyPlatformVariant: z11.optional(z11.string())
  })
]);
var AssessmentUrlInfoSchema = z11.object({
  url: z11.string(),
  hostname: z11.string(),
  lmsProvidedCourseId: z11.string(),
  lmsProvidedAssessmentId: z11.string()
});
var AssessmentUrlInfoWithInstituteIdSchema = AssessmentUrlInfoSchema.extend({
  instituteId: z11.optional(z11.number())
});
var ChromeOsAccessModeSchema = z11.union([z11.literal("managed-guest-session"), z11.literal("all")]);
var ProctoringVerityOptionsSchema = z11.object({
  enforceLockdown: z11.boolean(),
  enforceMobile: z11.boolean(),
  isExtension: z11.boolean(),
  isLockdown: z11.boolean(),
  isMobile: z11.boolean(),
  isChromeOs: z11.boolean(),
  isBrowser: z11.boolean(),
  videoRecordingMode: RecordingModeSchema,
  secondaryVideoRecordingMode: RecordingModeSchema,
  audioRecordingMode: RecordingModeSchema,
  recordScreen: z11.boolean(),
  recordWebTraffic: z11.boolean(),
  detectSidePanel: z11.boolean(),
  excludedWebTrafficRecordingUrls: z11.nullish(z11.array(z11.string())),
  recordClipboard: z11.boolean(),
  recordInput: z11.boolean(),
  oneScreenEnforcementMode: RecordingModeSchema,
  disableNewTab: z11.boolean(),
  closeOpenTabs: z11.boolean(),
  tabWhiteList: z11.nullish(z11.array(z11.string())),
  tabBlockList: z11.nullish(z11.array(z11.string())),
  keyboardShortcutBlockList: z11.nullish(z11.array(z11.string())),
  disableClipboardMode: z11.enum(["off", "tab", "system"]),
  enableGuidedAccessMode: z11.boolean(),
  restrictHandoffMode: RecordingModeSchema,
  disableSpellcheck: z11.boolean(),
  disablePrinting: z11.boolean(),
  disableRightClick: z11.boolean(),
  extensionBlockMode: RecordingModeSchema,
  allowedExtensionsList: z11.nullish(z11.array(z11.string())),
  logInstalledExtensions: z11.boolean(),
  disableDownloadFiles: z11.boolean(),
  hideOtherWindows: z11.boolean(),
  showRecordingPanel: z11.boolean(),
  idVerification: z11.boolean(),
  idVerificationEnableAi: z11.boolean(),
  faceTrackingMode: RecordingModeSchema,
  objectDetectionMode: RecordingModeSchema,
  applicationBlockMode: RecordingModeSchema,
  blockWindowedApplications: z11.boolean(),
  applicationBlockList: z11.nullish(z11.array(z11.string())),
  allowedApplicationsList: z11.nullish(z11.array(z11.string())),
  blockVirtualMachines: z11.boolean(),
  recordApplicationsActivity: z11.boolean(),
  logRunningApplications: z11.boolean(),
  isLiveProctored: z11.boolean(),
  isLivePopIn: z11.boolean(),
  enablePostReview: z11.boolean(),
  scanRoom: z11.boolean(),
  quietEnvironment: z11.boolean(),
  enableCalculator: z11.enum(["off", "scientific", "standard"]),
  enableWhiteboard: z11.boolean(),
  enableCodeEditor: z11.boolean(),
  codeEditorRoomId: z11.nullish(z11.array(z11.string()).max(1)),
  uploadScratchNotes: z11.boolean(),
  idVerificationMode: RecordingModeSchema,
  restrictDevices: z11.boolean(),
  audioTranscription: z11.boolean(),
  flaggedKeywords: z11.union([
    z11.null(),
    z11.array(
      z11.object({
        keyword: z11.string(),
        level: z11.number().optional()
      })
    )
  ]),
  uploadSignature: z11.boolean(),
  enableGeofence: z11.boolean(),
  geofenceLocations: z11.nullish(
    z11.array(
      z11.object({
        longitude: z11.number(),
        latitude: z11.number(),
        radius: z11.number(),
        name: z11.string(),
        isMetric: z11.boolean()
      })
    )
  ),
  disableVirtualCamera: z11.boolean(),
  enableIpAddressRestriction: z11.boolean(),
  allowedIpAddresses: z11.nullish(
    z11.array(
      z11.object({
        min: z11.string(),
        max: z11.string()
      })
    )
  ),
  enableManualEndProctoring: z11.boolean(),
  enableReferenceMaterial: z11.boolean(),
  referenceMaterialMode: z11.union([z11.literal("off"), z11.literal("instructor"), z11.literal("student")]),
  enabled: z11.boolean(),
  requiresIncognito: z11.boolean(),
  chromeOsAccess: z11.nullish(ChromeOsAccessModeSchema),
  isChromeExtension: z11.boolean(),
  isFirefoxExtension: z11.boolean(),
  isSafariExtension: z11.boolean(),
  automaticallyAddedAllowedUrls: z11.nullish(z11.array(z11.string())),
  logEvents: z11.boolean(),
  delayRecordingUntilAssessmentStart: z11.boolean(),
  paymentEnabled: z11.boolean(),
  preferredSetupUiVersion: z11.string(),
  password: z11.nullish(z11.string()),
  thirdPartyAssessmentOptions: z11.nullish(
    z11.object({
      thirdPartyUrl: z11.nullish(z11.string()),
      password: z11.nullish(z11.string()),
      thirdPartyPlatformId: z11.nullish(z11.number())
    })
  )
});
var InterceptorCapabilitiesSchema = z11.object({
  requestLmsApiToken: z11.optional(z11.boolean()),
  consumeSetupResults: z11.optional(z11.boolean())
});
var ProctoringSetupPageInfoSchema = z11.object({
  browserType: BrowserTypeSchema,
  extensionArch: z11.optional(BrowserArchSchema),
  platformInfo: PlatformInfoSchema,
  lmsDetails: LmsDetailsSchema,
  assessmentInfo: AssessmentUrlInfoWithInstituteIdSchema,
  proctoringOptions: z11.optional(ProctoringVerityOptionsSchema),
  takeAssessmentGuideActive: z11.boolean(),
  interceptorCapabilities: z11.optional(InterceptorCapabilitiesSchema)
});
var ProctoringSetupResultsSchema = z11.object({
  setupState: z11.optional(
    z11.array(
      z11.object({
        key: z11.string(),
        status: z11.string(),
        options: z11.optional(z11.unknown()),
        state: z11.optional(z11.unknown()),
        error: z11.optional(z11.unknown())
      })
    )
  )
});
var proctoringSetupPageInterceptorMessagesDefinition = {
  topic: "proctoring-setup-page_interceptor",
  schema: {
    ping: { request: z11.void(), response: z11.string() },
    getOptions: { request: z11.void(), response: ProctoringSetupPageInfoSchema },
    initialized: { request: z11.void(), response: z11.void() },
    refresh: { request: z11.void(), response: z11.void() },
    showStartButton: { request: z11.void(), response: z11.void() },
    hideStartButton: { request: z11.void(), response: z11.void() },
    enableStartButton: { request: z11.void(), response: z11.void() },
    disableStartButton: { request: z11.void(), response: z11.void() },
    cancelSetup: { request: z11.union([ProctoringSetupResultsSchema, z11.void()]), response: z11.void() },
    requestLmsApiToken: { request: z11.object({ authorizationUrl: z11.string() }), response: z11.void() },
    startAssessment: {
      request: z11.union([ProctoringSetupResultsSchema, z11.void()]),
      response: z11.void()
    }
  }
};

// src/contracts/proctoring-message/setup-page.ts
import { z as z12 } from "zod";
var interceptorProctoringSetupPageMessagesDefinition = {
  topic: "interceptor_proctoring-setup-page",
  schema: {
    onStartButtonClick: { request: z12.void(), response: z12.void() }
  }
};

// src/contracts/proctoring-message/platform.ts
import { z as z28 } from "zod";

// src/contracts/proctoring-message/proctoring-component/index.ts
import z26 from "zod";

// src/contracts/proctoring-message/proctoring-component/definition.ts
import z13 from "zod";
var ProctoringTaskIdSchema = z13.string();
function createProctoringComponentStateEventSchema(definition) {
  return z13.object({
    taskId: ProctoringTaskIdSchema,
    componentType: z13.literal(definition.type),
    componentId: definition.IdSchema,
    componentState: definition.StateEventDetailsSchema
  });
}

// src/contracts/proctoring-message/proctoring-component/recorder/display-info-recorder/definition.ts
import z15 from "zod";

// src/contracts/proctoring-message/proctoring-component/recorder/definition.ts
import z14 from "zod";
var BasePendingRecordingStateSchema = z14.object({ status: z14.literal("pending") });
var BaseFailedRecordingStateSchema = z14.object({ status: z14.literal("failed") });
var BaseActiveRecordingStateSchema = z14.object({ status: z14.literal("active") });
var BaseStoppingRecordingStateSchema = z14.object({ status: z14.literal("stopping") });
var BaseStoppedRecordingStateSchema = z14.object({ status: z14.literal("stopped") });
var baseRecordingStateSchemas = {
  PendingStateSchema: BasePendingRecordingStateSchema,
  FailedStateSchema: BaseFailedRecordingStateSchema,
  ActiveStateSchema: BaseActiveRecordingStateSchema,
  StoppingStateSchema: BaseStoppingRecordingStateSchema,
  StoppedStateSchema: BaseStoppedRecordingStateSchema
};
function constructRecordingStateSchema({
  PendingStateSchema,
  FailedStateSchema,
  ActiveStateSchema,
  StoppingStateSchema,
  StoppedStateSchema
}) {
  return z14.discriminatedUnion("status", [
    PendingStateSchema,
    FailedStateSchema,
    ActiveStateSchema,
    StoppingStateSchema,
    StoppedStateSchema
  ]);
}
var BaseRecordingStateSchema = constructRecordingStateSchema(baseRecordingStateSchemas);
function constructRecordingStateEventDetailsSchema({
  PendingStateSchema,
  FailedStateSchema,
  ActiveStateSchema,
  StoppingStateSchema,
  StoppedStateSchema
}) {
  const ActiveStateEventDetailsSchema = ActiveStateSchema.extend({
    updating: z14.boolean(),
    valid: z14.boolean()
  });
  return z14.discriminatedUnion("status", [
    PendingStateSchema,
    FailedStateSchema,
    ActiveStateEventDetailsSchema,
    StoppingStateSchema,
    StoppedStateSchema
  ]).and(z14.object({ error: z14.optional(z14.unknown()) }));
}
var BaseRecordingStateEventDetailsSchema = constructRecordingStateEventDetailsSchema(baseRecordingStateSchemas);

// src/contracts/proctoring-message/proctoring-component/recorder/display-info-recorder/definition.ts
function addSharedState(Schema) {
  return Schema.extend({ enforcementMode: RecordingModeSchema });
}
var stateSchemas = {
  PendingStateSchema: addSharedState(BasePendingRecordingStateSchema),
  FailedStateSchema: addSharedState(BaseFailedRecordingStateSchema),
  ActiveStateSchema: addSharedState(
    BaseActiveRecordingStateSchema.extend({
      displays: z15.array(z15.unknown()),
      ignored: z15.boolean()
    })
  ),
  StoppingStateSchema: addSharedState(BaseStoppingRecordingStateSchema),
  StoppedStateSchema: addSharedState(BaseStoppedRecordingStateSchema)
};
var definitionWithoutType = {
  IdSchema: z15.string().brand("DisplayInfoRecordingId"),
  OptionsSchema: z15.object({ enforcementMode: RecordingModeSchema, logEvents: z15.boolean() }),
  ...stateSchemas,
  StateSchema: constructRecordingStateSchema(stateSchemas),
  StateEventDetailsSchema: constructRecordingStateEventDetailsSchema(stateSchemas)
};
var displayInfoRecorderDefinition = {
  ...definitionWithoutType,
  type: "displayInfoRecorder"
};

// src/contracts/proctoring-message/proctoring-component/recorder/browser-info-recorder/definition.ts
import z16 from "zod";
var ExtensionTypeSchema = z16.union([
  z16.literal("extension"),
  z16.literal("login_screen_extension"),
  z16.literal("theme"),
  z16.literal("hosted_app"),
  z16.literal("packaged_app"),
  z16.literal("legacy_packaged_app")
]);
var ExtensionIconInfoSchema = z16.object({
  size: z16.number(),
  url: z16.string()
});
var InstalledExtensionInfoSchema = z16.object({
  id: z16.string(),
  enabled: z16.boolean(),
  type: ExtensionTypeSchema,
  name: z16.string(),
  icons: z16.optional(z16.array(ExtensionIconInfoSchema)),
  allowed: z16.boolean()
});
var TabInfoSchema = z16.object({
  id: z16.number(),
  url: z16.optional(z16.string()),
  pendingUrl: z16.optional(z16.string()),
  title: z16.optional(z16.string()),
  allowed: z16.boolean()
});
var stateSchemas2 = {
  PendingStateSchema: BasePendingRecordingStateSchema,
  FailedStateSchema: BaseFailedRecordingStateSchema,
  ActiveStateSchema: BaseActiveRecordingStateSchema.extend({
    extensions: z16.optional(
      z16.object({
        state: z16.array(InstalledExtensionInfoSchema),
        valid: z16.boolean(),
        ignored: z16.boolean()
      })
    ),
    tabs: z16.optional(
      z16.object({
        state: z16.array(TabInfoSchema),
        valid: z16.boolean(),
        ignored: z16.boolean()
      })
    ),
    sidePanel: z16.optional(
      z16.object({
        open: z16.boolean(),
        valid: z16.boolean(),
        ignored: z16.boolean()
      })
    )
  }),
  StoppingStateSchema: BaseStoppingRecordingStateSchema,
  StoppedStateSchema: BaseStoppedRecordingStateSchema
};
var definitionWithoutType2 = {
  IdSchema: z16.string().brand("BrowserInfoRecordingId"),
  OptionsSchema: z16.object({
    extensionBlockMode: RecordingModeSchema,
    allowedExtensions: z16.array(z16.string()),
    closeOpenTabs: z16.boolean(),
    tabWhiteList: z16.array(z16.string()),
    detectSidePanel: z16.boolean()
  }),
  ...stateSchemas2,
  StateSchema: constructRecordingStateSchema(stateSchemas2),
  StateEventDetailsSchema: constructRecordingStateEventDetailsSchema(stateSchemas2)
};
var browserInfoRecorderDefinition = {
  ...definitionWithoutType2,
  type: "browserInfoRecorder"
};

// src/contracts/proctoring-message/proctoring-component/recorder/display-recorder/definition.ts
import z17 from "zod";
var BaseSourceSchema = z17.object({
  sourceIndex: z17.number(),
  streamUpdating: z17.boolean(),
  streamError: z17.optional(z17.unknown()),
  stream: z17.optional(
    z17.object({
      id: z17.string(),
      active: z17.boolean(),
      settings: BaseMediaTrackSettingsSchema.extend({
        displaySurface: z17.string()
      }).partial()
    })
  ),
  valid: z17.boolean(),
  ignored: z17.boolean(),
  brokenVideoDetectionActive: z17.boolean().optional().default(false),
  brokenVideoDetected: z17.boolean().optional().default(false)
});
var baseSourcesSchema = {
  sources: z17.array(BaseSourceSchema)
};
var stateSchemas3 = {
  PendingStateSchema: BasePendingRecordingStateSchema.extend(baseSourcesSchema),
  FailedStateSchema: BaseFailedRecordingStateSchema,
  ActiveStateSchema: BaseActiveRecordingStateSchema.extend(baseSourcesSchema),
  StoppingStateSchema: BaseStoppingRecordingStateSchema.extend({
    sources: z17.array(BaseSourceSchema.extend({ uploadProgress: z17.number() }))
  }),
  StoppedStateSchema: BaseStoppedRecordingStateSchema
};
var definitionWithoutType3 = {
  IdSchema: z17.string().brand("DisplayRecordingId"),
  OptionsSchema: z17.object({
    uploadInterval: z17.optional(z17.number()),
    delayRecordingUntilAssessmentStart: z17.boolean(),
    logEvents: z17.boolean(),
    disableCheckForBrokenVideo: z17.boolean().optional().default(false)
  }),
  ...stateSchemas3,
  StateSchema: constructRecordingStateSchema(stateSchemas3),
  StateEventDetailsSchema: constructRecordingStateEventDetailsSchema(stateSchemas3)
};
var displayRecorderDefinition = {
  ...definitionWithoutType3,
  type: "displayRecorder"
};

// src/contracts/proctoring-message/proctoring-component/recorder/user-media-recorder/definition.ts
import z18 from "zod";
var SourceIndexSchema = z18.number();
function constructSourcesSchemaShape(sourceSchema) {
  return { sources: z18.array(sourceSchema) };
}
var TrackInfoSchema = z18.object({
  recordingMode: EnabledRecordingModeSchema,
  device: z18.optional(z18.object({ deviceId: z18.string(), groupId: z18.optional(z18.string()) })),
  streamUpdating: z18.boolean(),
  streamError: z18.optional(z18.unknown()),
  stream: z18.optional(
    z18.object({
      id: z18.string(),
      active: z18.boolean(),
      settings: BaseMediaTrackSettingsSchema.extend({
        facingMode: z18.string()
      }).partial()
    })
  ),
  valid: z18.boolean(),
  ignored: z18.boolean()
});
var BaseSourceSchema2 = z18.object({
  sourceIndex: SourceIndexSchema,
  tracks: z18.object({
    audio: z18.optional(TrackInfoSchema),
    video: z18.optional(
      TrackInfoSchema.extend({
        disableVirtualCamera: z18.boolean(),
        brokenVideoDetectionActive: z18.boolean(),
        brokenVideoDetected: z18.boolean()
      })
    )
  })
});
var sourcesSchemaShape = constructSourcesSchemaShape(BaseSourceSchema2);
var stateSchemas4 = {
  PendingStateSchema: BasePendingRecordingStateSchema.extend(sourcesSchemaShape),
  FailedStateSchema: BaseFailedRecordingStateSchema.extend(sourcesSchemaShape),
  ActiveStateSchema: BaseActiveRecordingStateSchema.extend(sourcesSchemaShape),
  StoppingStateSchema: BaseStoppingRecordingStateSchema.extend(
    constructSourcesSchemaShape(BaseSourceSchema2.extend({ uploadProgress: z18.number() }))
  ),
  StoppedStateSchema: BaseStoppedRecordingStateSchema.extend(sourcesSchemaShape)
};
var definitionWithoutType4 = {
  IdSchema: z18.string().brand("UserMediaRecordingId"),
  OptionsSchema: z18.object({
    logEvents: z18.boolean(),
    delayRecordingUntilAssessmentStart: z18.boolean(),
    sources: z18.array(
      z18.object({
        recordingSourceIndex: z18.optional(SourceIndexSchema),
        uploadInterval: z18.optional(z18.number()),
        videoRecordingMode: RecordingModeSchema,
        audioRecordingMode: RecordingModeSchema,
        disableVirtualCamera: z18.boolean(),
        disableCheckForBrokenVideo: z18.boolean()
      })
    )
  }),
  ...stateSchemas4,
  StateSchema: constructRecordingStateSchema(stateSchemas4),
  StateEventDetailsSchema: constructRecordingStateEventDetailsSchema(stateSchemas4)
};
var userMediaRecorderDefinition = {
  ...definitionWithoutType4,
  type: "userMediaRecorder"
};

// src/contracts/proctoring-message/proctoring-component/recorder/restricted-device-recorder/definition.ts
import z19 from "zod";
var BaseDeviceStateSchema = z19.object({ deviceId: z19.string() });
var RestrictedDevicePublicStateSchema = z19.object({
  devices: z19.object({
    self: BaseDeviceStateSchema.extend({
      connectionStatus: z19.enum(["spawned", "connecting", "connected", "disconnected", "error"])
    }),
    other: BaseDeviceStateSchema.extend({
      appDeepLinkUrl: z19.string(),
      connectionStatus: z19.enum(["spawned", "connected", "disconnected"]),
      locked: z19.boolean(),
      recordingStatus: z19.optional(
        z19.object({
          video: z19.optional(
            z19.object({
              selectedCamera: z19.optional(z19.union([z19.literal("front"), z19.literal("back")])),
              isRecording: z19.boolean()
            })
          )
        })
      )
    })
  })
});
var sharedState = {
  restrictDevices: z19.boolean(),
  videoRecordingMode: RecordingModeSchema
};
var stateSchemas5 = {
  PendingStateSchema: BasePendingRecordingStateSchema.extend(sharedState),
  FailedStateSchema: BaseFailedRecordingStateSchema.extend(sharedState),
  ActiveStateSchema: BaseActiveRecordingStateSchema.extend(sharedState).extend(RestrictedDevicePublicStateSchema.shape).extend({ ignored: z19.boolean() }),
  StoppingStateSchema: BaseStoppingRecordingStateSchema.extend(sharedState),
  StoppedStateSchema: BaseStoppedRecordingStateSchema.extend(sharedState)
};
var definitionWithoutType5 = {
  IdSchema: z19.string().brand("RestrictedDeviceRecordingId"),
  OptionsSchema: z19.object({
    restrictDevices: z19.boolean(),
    videoRecordingMode: RecordingModeSchema
  }),
  ...stateSchemas5,
  StateSchema: constructRecordingStateSchema(stateSchemas5),
  StateEventDetailsSchema: constructRecordingStateEventDetailsSchema(stateSchemas5)
};
var restrictedDeviceRecorderDefinition = {
  ...definitionWithoutType5,
  type: "restrictedDeviceRecorder"
};

// src/contracts/proctoring-message/proctoring-component/recorder/system-info-recorder/definition.ts
import z20 from "zod";
var ApplicationInfoSchema = z20.object({
  name: z20.string(),
  hasWindow: z20.boolean(),
  allowed: z20.boolean(),
  ignored: z20.boolean()
});
var SettingsSchema = z20.object({
  handoff: z20.optional(
    z20.object({
      enforcementMode: EnabledRecordingModeSchema,
      value: z20.boolean(),
      valid: z20.boolean(),
      ignored: z20.boolean()
    })
  )
});
var stateSchemas6 = {
  PendingStateSchema: BasePendingRecordingStateSchema,
  FailedStateSchema: BaseFailedRecordingStateSchema,
  ActiveStateSchema: BaseActiveRecordingStateSchema.extend({
    runningApplications: z20.optional(
      z20.object({
        enforcementMode: RecordingModeSchema,
        state: z20.array(ApplicationInfoSchema),
        valid: z20.boolean(),
        ignored: z20.boolean()
      })
    ),
    settings: z20.optional(
      z20.object({
        state: SettingsSchema,
        valid: z20.boolean(),
        ignored: z20.boolean()
      })
    )
  }),
  StoppingStateSchema: BaseStoppingRecordingStateSchema,
  StoppedStateSchema: BaseStoppedRecordingStateSchema
};
var definitionWithoutType6 = {
  IdSchema: z20.string().brand("SystemInfoRecordingId"),
  OptionsSchema: z20.object({
    recordWindowedApplications: z20.boolean(),
    applicationBlockMode: RecordingModeSchema,
    blockWindowedApplications: z20.boolean(),
    applicationBlockList: z20.array(z20.string()),
    allowedApplicationsList: z20.array(z20.string()),
    restrictHandoffMode: RecordingModeSchema,
    logEvents: z20.boolean()
  }),
  ...stateSchemas6,
  StateSchema: constructRecordingStateSchema(stateSchemas6),
  StateEventDetailsSchema: constructRecordingStateEventDetailsSchema(stateSchemas6)
};
var systemInfoRecorderDefinition = {
  ...definitionWithoutType6,
  type: "systemInfoRecorder"
};

// src/contracts/proctoring-message/proctoring-component/checker/performance-checker/definition.ts
import z22 from "zod";

// src/contracts/proctoring-message/proctoring-component/checker/definition.ts
import z21 from "zod";
var BasePendingCheckStateSchema = z21.object({ status: z21.literal("pending") });
var BaseFailedCheckStateSchema = z21.object({ status: z21.literal("failed") });
var BaseCompletedCheckStateSchema = z21.object({ status: z21.literal("completed") });
var baseCheckStateSchemas = {
  PendingStateSchema: BasePendingCheckStateSchema,
  FailedStateSchema: BaseFailedCheckStateSchema,
  CompletedStateSchema: BaseCompletedCheckStateSchema
};
function constructCheckStateSchema({
  PendingStateSchema,
  FailedStateSchema,
  CompletedStateSchema
}) {
  return z21.discriminatedUnion("status", [PendingStateSchema, FailedStateSchema, CompletedStateSchema]);
}
var BaseCheckStateSchema = constructCheckStateSchema(baseCheckStateSchemas);
function constructCheckStateEventDetailsSchema({
  PendingStateSchema,
  FailedStateSchema,
  CompletedStateSchema
}) {
  const CompletedStateEventDetailsSchema = CompletedStateSchema.extend({
    valid: z21.boolean()
  });
  return z21.discriminatedUnion("status", [PendingStateSchema, FailedStateSchema, CompletedStateEventDetailsSchema]).and(z21.object({ error: z21.optional(z21.unknown()) }));
}
var BaseCheckStateEventDetailsSchema = constructCheckStateEventDetailsSchema(baseCheckStateSchemas);

// src/contracts/proctoring-message/proctoring-component/checker/performance-checker/definition.ts
var stateSchemas7 = {
  PendingStateSchema: BasePendingCheckStateSchema,
  FailedStateSchema: BaseFailedCheckStateSchema,
  CompletedStateSchema: BaseCompletedCheckStateSchema.extend({
    forceContinued: z22.boolean(),
    cpuUsage: z22.object({ value: z22.number(), reference: z22.number(), valid: z22.boolean() }),
    memoryUsage: z22.object({ value: z22.number(), reference: z22.number(), valid: z22.boolean() })
  })
};
var definitionWithoutType7 = {
  IdSchema: z22.string().brand("PerformanceCheckId"),
  OptionsSchema: z22.object({ logEvents: z22.boolean(), forceContinue: z22.boolean() }),
  ...stateSchemas7,
  StateSchema: constructCheckStateSchema(stateSchemas7),
  StateEventDetailsSchema: constructCheckStateEventDetailsSchema(stateSchemas7)
};
var performanceCheckerDefinition = {
  ...definitionWithoutType7,
  type: "performanceChecker"
};

// src/contracts/proctoring-message/proctoring-component/checker/network-checker/definition.ts
import z23 from "zod";
var stateSchemas8 = {
  PendingStateSchema: BasePendingCheckStateSchema,
  FailedStateSchema: BaseFailedCheckStateSchema,
  CompletedStateSchema: BaseCompletedCheckStateSchema.extend({
    forceContinued: z23.boolean(),
    uploadSpeed: z23.object({ value: z23.number(), reference: z23.number(), valid: z23.boolean() })
  })
};
var definitionWithoutType8 = {
  IdSchema: z23.string().brand("NetworkCheckId"),
  OptionsSchema: z23.object({ logEvents: z23.boolean(), forceContinue: z23.boolean() }),
  ...stateSchemas8,
  StateSchema: constructCheckStateSchema(stateSchemas8),
  StateEventDetailsSchema: constructCheckStateEventDetailsSchema(stateSchemas8)
};
var networkCheckerDefinition = {
  ...definitionWithoutType8,
  type: "networkChecker"
};

// src/contracts/proctoring-message/proctoring-component/checker/vm-checker/definition.ts
import z24 from "zod";
var stateSchemas9 = {
  PendingStateSchema: BasePendingCheckStateSchema,
  FailedStateSchema: BaseFailedCheckStateSchema,
  CompletedStateSchema: BaseCompletedCheckStateSchema.extend({
    isVm: z24.boolean()
  })
};
var definitionWithoutType9 = {
  IdSchema: z24.string().brand("VmCheckId"),
  OptionsSchema: z24.object({}),
  ...stateSchemas9,
  StateSchema: constructCheckStateSchema(stateSchemas9),
  StateEventDetailsSchema: constructCheckStateEventDetailsSchema(stateSchemas9)
};
var vmCheckerDefinition = {
  ...definitionWithoutType9,
  type: "vmChecker"
};

// src/contracts/proctoring-message/proctoring-component/recorder/user-media-recorder/index.ts
import { z as z25 } from "zod";
var DeviceTypeSchema = z25.union([z25.literal("video"), z25.literal("audio")]);
var UserMediaDevicePathSchema = z25.object({
  recordingId: userMediaRecorderDefinition.IdSchema,
  sourceIndex: z25.number(),
  deviceType: DeviceTypeSchema
});
var SelectUserMediaDeviceOptionsSchema = UserMediaDevicePathSchema.extend({
  device: z25.optional(
    z25.object({
      deviceId: z25.string(),
      groupId: z25.optional(z25.string())
    })
  )
});

// src/contracts/proctoring-message/proctoring-component/index.ts
var ProctoringComponentStateMessageSchema = z26.object({
  type: z26.literal("proctoring-component-state-updated"),
  details: z26.discriminatedUnion("componentType", [
    createProctoringComponentStateEventSchema(displayInfoRecorderDefinition),
    createProctoringComponentStateEventSchema(displayRecorderDefinition),
    createProctoringComponentStateEventSchema(userMediaRecorderDefinition),
    createProctoringComponentStateEventSchema(restrictedDeviceRecorderDefinition),
    createProctoringComponentStateEventSchema(systemInfoRecorderDefinition),
    createProctoringComponentStateEventSchema(browserInfoRecorderDefinition),
    createProctoringComponentStateEventSchema(performanceCheckerDefinition),
    createProctoringComponentStateEventSchema(networkCheckerDefinition),
    createProctoringComponentStateEventSchema(vmCheckerDefinition)
  ])
});

// src/contracts/proctoring-message/media-stream-constraints.ts
import { z as z27 } from "zod";
function constructConstraintValueSchema(Schema) {
  return z27.union([Schema, z27.object({ ideal: Schema, exact: Schema }).partial()]);
}
var ConstraintNumberValueSchema = constructConstraintValueSchema(z27.number());
var ConstraintStringValueSchema = constructConstraintValueSchema(z27.union([z27.string(), z27.array(z27.string())]));
var BaseDeviceConstraintsSchema = z27.object({
  deviceId: ConstraintStringValueSchema,
  groupId: ConstraintStringValueSchema
}).partial();
var MediaStreamConstraintsSchema = z27.object({
  video: z27.union([
    z27.boolean(),
    BaseDeviceConstraintsSchema.extend({
      width: ConstraintNumberValueSchema,
      height: ConstraintNumberValueSchema,
      frameRate: ConstraintNumberValueSchema
    }).partial()
  ]),
  audio: z27.union([z27.boolean(), BaseDeviceConstraintsSchema])
}).partial();

// src/contracts/proctoring-message/platform.ts
function constructProctoringComponentResponseSchema(definition) {
  return z28.optional(z28.object({ options: definition.OptionsSchema }));
}
function constructComponentInitializationMessageSchema(definition) {
  return { request: definition.OptionsSchema, response: definition.IdSchema };
}
var ProctoringComponentInfoSchema = z28.object({
  displayInfoRecorder: constructProctoringComponentResponseSchema(displayInfoRecorderDefinition),
  displayRecorder: constructProctoringComponentResponseSchema(displayRecorderDefinition),
  userMediaRecorder: constructProctoringComponentResponseSchema(userMediaRecorderDefinition),
  browserInfoRecorder: z28.optional(
    z28.object({
      installedExtensions: z28.optional(z28.object({ options: browserInfoRecorderDefinition.OptionsSchema })),
      tabs: z28.optional(z28.object({ options: browserInfoRecorderDefinition.OptionsSchema })),
      sidePanel: z28.optional(z28.object({ options: browserInfoRecorderDefinition.OptionsSchema }))
    })
  ),
  restrictedDeviceRecorder: constructProctoringComponentResponseSchema(restrictedDeviceRecorderDefinition),
  systemInfoRecorder: z28.optional(
    z28.object({
      runningApplications: z28.optional(z28.object({ options: systemInfoRecorderDefinition.OptionsSchema })),
      settings: z28.optional(z28.object({ options: systemInfoRecorderDefinition.OptionsSchema }))
    })
  ),
  performanceChecker: constructProctoringComponentResponseSchema(performanceCheckerDefinition),
  networkChecker: constructProctoringComponentResponseSchema(networkCheckerDefinition),
  vmChecker: constructProctoringComponentResponseSchema(vmCheckerDefinition)
});
var DevicePermissionStatusSchema = z28.union([
  z28.literal("initial"),
  z28.literal("requested"),
  z28.literal("processing"),
  z28.literal("success"),
  z28.literal("failure")
]);
var DisplayPermissionUpdatedMessageSchema = z28.object({
  type: z28.literal("display-permission-changed"),
  details: z28.object({
    status: DevicePermissionStatusSchema
  })
});
var DevicePermissionUpdatedMessageSchema = z28.object({
  type: z28.literal("media-device-permission-changed"),
  details: z28.object({
    type: DeviceTypeSchema,
    status: DevicePermissionStatusSchema
  })
});
var DevicesUpdatedMessageSchema = z28.object({
  type: z28.literal("media-devices-changed"),
  details: z28.optional(z28.array(SerializableMediaDeviceInfoSchema))
});
var MediaStreamEndedMessageSchema = z28.object({
  type: z28.literal("media-stream-ended"),
  details: z28.object({ streamId: z28.string() })
});
var MediaStreamWebRtcMessageSchema = z28.object({
  type: z28.literal("media-stream-webrtc-message"),
  details: z28.discriminatedUnion("type", [
    z28.object({
      requestId: z28.string(),
      type: z28.literal("offer"),
      payload: z28.any()
    }),
    z28.object({ requestId: z28.string(), type: z28.literal("candidate"), payload: z28.any() })
  ])
});
var proctoringSetupPagePlatformMessagesDefinition = {
  topic: "proctoring-setup-page_platform",
  schema: {
    ping: { request: z28.void(), response: z28.string() },
    authenticate: {
      request: z28.void(),
      response: z28.object({
        accountId: z28.optional(z28.string()),
        authCode: z28.string()
      })
    },
    startProctoring: {
      request: z28.object({
        lmsDetails: LmsDetailsSchema,
        assessmentInfo: AssessmentUrlInfoSchema
      }),
      response: z28.object({
        taskId: ProctoringTaskIdSchema,
        proctoringSessionId: z28.optional(z28.string()),
        proctoringOptions: ProctoringVerityOptionsSchema,
        proctoringComponents: ProctoringComponentInfoSchema,
        focusRequiredForMediaDeviceAccess: z28.boolean()
      })
    },
    startDisplayInfoRecording: constructComponentInitializationMessageSchema(displayInfoRecorderDefinition),
    startInstalledExtensionsRecording: constructComponentInitializationMessageSchema(browserInfoRecorderDefinition),
    startTabsRecording: constructComponentInitializationMessageSchema(browserInfoRecorderDefinition),
    startSidePanelRecording: constructComponentInitializationMessageSchema(browserInfoRecorderDefinition),
    closeBlockedTabs: {
      request: z28.object({ recordingId: browserInfoRecorderDefinition.IdSchema }),
      response: z28.void()
    },
    startDisplayRecording: constructComponentInitializationMessageSchema(displayRecorderDefinition),
    getDisplayPermissionStatus: {
      request: z28.object({}),
      response: DevicePermissionStatusSchema
    },
    startUserMediaRecording: constructComponentInitializationMessageSchema(userMediaRecorderDefinition),
    selectUserMediaDevice: {
      request: SelectUserMediaDeviceOptionsSchema,
      response: z28.void()
    },
    retryUserMediaStreamCreation: {
      request: UserMediaDevicePathSchema,
      response: z28.void()
    },
    activateUserMediaRecording: {
      request: userMediaRecorderDefinition.IdSchema,
      response: z28.void()
    },
    getMediaDevicePermissionStatus: {
      request: z28.object({ type: DeviceTypeSchema }),
      response: DevicePermissionStatusSchema
    },
    requestMediaDevicePermission: {
      request: z28.object({ type: DeviceTypeSchema }),
      response: z28.void()
    },
    getMediaDevices: {
      request: z28.object({}),
      response: z28.union([z28.array(SerializableMediaDeviceInfoSchema), z28.undefined()])
    },
    createUserMediaStream: {
      request: MediaStreamConstraintsSchema,
      response: z28.string()
    },
    deleteMediaStream: {
      request: z28.object({ streamId: z28.string() }),
      response: z28.void()
    },
    requestMediaStreamConnection: {
      request: z28.object({ requestId: z28.string(), streamId: z28.string(), fps: z28.optional(z28.number()) }),
      response: z28.void()
    },
    sendWebRtcMessage: {
      request: z28.discriminatedUnion("type", [
        z28.object({
          requestId: z28.string(),
          type: z28.literal("answer"),
          payload: z28.any()
        }),
        z28.object({ requestId: z28.string(), type: z28.literal("candidate"), payload: z28.any() })
      ]),
      response: z28.void()
    },
    disposeMediaStreamConnection: {
      request: z28.object({ requestId: z28.string() }),
      response: z28.void()
    },
    startRestrictedDeviceRecording: constructComponentInitializationMessageSchema(restrictedDeviceRecorderDefinition),
    stopRestrictedDeviceRecording: {
      request: z28.object({ recordingId: restrictedDeviceRecorderDefinition.IdSchema }),
      response: z28.void()
    },
    startRunningApplicationsRecording: constructComponentInitializationMessageSchema(systemInfoRecorderDefinition),
    forceQuitBlockedApplications: {
      request: z28.object({ recordingId: systemInfoRecorderDefinition.IdSchema }),
      response: z28.void()
    },
    startSystemSettingsRecording: constructComponentInitializationMessageSchema(systemInfoRecorderDefinition),
    disableHandoffSettings: {
      request: z28.object({ recordingId: systemInfoRecorderDefinition.IdSchema }),
      response: z28.void()
    },
    checkPerformance: constructComponentInitializationMessageSchema(performanceCheckerDefinition),
    runNetworkCheck: constructComponentInitializationMessageSchema(networkCheckerDefinition),
    checkForVm: constructComponentInitializationMessageSchema(vmCheckerDefinition),
    completeProctoringTaskInitialization: { request: z28.object({ taskId: ProctoringTaskIdSchema }), response: z28.void() }
  }
};

// src/contracts/proctoring-message/lms-oauth-redirect-page.ts
import { z as z29 } from "zod";
var lmsOauthRedirectPageInterceptorMessagesDefinition = {
  topic: "lms-oauth-redirect-page_interceptor",
  schema: {
    onComplete: { request: z29.object({ error: z29.optional(z29.string()) }), response: z29.void() }
  }
};

// src/contracts/admin/admin-http.ts
import * as z32 from "zod/v4";

// ../database/dist/prisma/gen/zod/index.js
import { z as z30 } from "zod";
var TransactionIsolationLevelSchema = z30.enum(["ReadUncommitted", "ReadCommitted", "RepeatableRead", "Serializable"]);
var GlobalSettingScalarFieldEnumSchema = z30.enum(["key", "value"]);
var ResourceScalarFieldEnumSchema = z30.enum(["id", "name", "createdAt", "updatedAt"]);
var RoleScalarFieldEnumSchema = z30.enum(["id", "name", "type", "createdAt", "updatedAt"]);
var RoleResourcePermissionScalarFieldEnumSchema = z30.enum(["id", "roleId", "resourceId", "permission", "createdAt", "updatedAt"]);
var InstituteUserRoleScalarFieldEnumSchema = z30.enum(["id", "instituteUserId", "roleId", "createdAt", "updatedAt"]);
var CourseUserRoleScalarFieldEnumSchema = z30.enum(["id", "courseUserId", "roleId", "createdAt", "updatedAt"]);
var LmsCourseScalarFieldEnumSchema = z30.enum(["id", "instituteId", "courseId", "courseInfo", "accessControlUuid", "createdAt", "updatedAt", "verityEnabled", "subAccountId"]);
var LmsCourseUserScalarFieldEnumSchema = z30.enum(["id", "userId", "courseId", "viewVideos", "createdAt", "updatedAt"]);
var LmsQuizScalarFieldEnumSchema = z30.enum(["id", "courseId", "quizId", "quizInfo", "other", "isDeleted", "summary", "createdAt", "updatedAt", "sourcesLastScanned", "ltiPayload"]);
var VerityAssessmentScalarFieldEnumSchema = z30.enum(["quizId", "type", "isPublished", "syncGradebook", "startTime", "endTime", "allowLateSubmission", "lateSubmissionDate", "shuffleQuestions", "questionsPerPage", "numAttempts", "showScoreOnSubmit", "duration", "description", "moduleId", "scoringStrategy", "createdAt", "updatedAt"]);
var AssessmentQuestionScalarFieldEnumSchema = z30.enum(["id", "assessmentId", "lmsProvidedQuestionId", "title", "bodyHtml", "bodyText", "sources", "numSources", "decoySiteHostname", "decoySitePostId", "isDeleted", "createdAt", "updatedAt"]);
var QuestionSourcesScalarFieldEnumSchema = z30.enum(["id", "hostname", "email", "createdAt", "updatedAt"]);
var LmsQuizCacheScalarFieldEnumSchema = z30.enum(["id", "quizId", "courseId", "platformId", "activityUrl", "createdAt", "updatedAt"]);
var LmsQuizCopyCheckedScalarFieldEnumSchema = z30.enum(["id", "instituteId", "lmsCourseId", "quizId", "createdAt"]);
var Lti1p3InstituteScalarFieldEnumSchema = z30.enum(["id", "instituteName", "lms", "language", "origin", "issId", "ltiClientId", "lmsApiClientId", "lmsApiClientSecret", "lmsApiIncludeD2LCorePermission", "deploymentId", "accessControlUuid", "isDisabled", "createdAt", "updatedAt", "institutionType", "accountType"]);
var InstitutePendingDeletionScalarFieldEnumSchema = z30.enum(["id", "instituteId", "initiatingUserId", "initiatingUserName", "initiatingUserEmail", "deletionDate", "status", "createdAt", "updatedAt"]);
var LuminaPartnerKeyScalarFieldEnumSchema = z30.enum(["id", "keyId", "keyHash", "label", "createdAt", "lastUsedAt", "revokedAt"]);
var LuminaResultsTokenScalarFieldEnumSchema = z30.enum(["id", "tokenId", "instituteId", "courseId", "lmsQuizId", "studentId", "createdAt", "expiresAt", "redeemedAt"]);
var LtiDeploymentHostnameScalarFieldEnumSchema = z30.enum(["id", "ltiDeploymentId", "hostname", "createdAt", "updatedAt"]);
var Lti1p3IssScalarFieldEnumSchema = z30.enum(["id", "iss", "platformOidcEndpoint", "platformJwksEndpoint", "createdAt", "updatedAt"]);
var ProctoringExemptionScalarFieldEnumSchema = z30.enum(["id", "assessmentId", "userId", "proctoringOptionProfileId", "accommodationNotes", "createdAt", "updatedAt"]);
var InstitutionProctoringAccommodationScalarFieldEnumSchema = z30.enum(["id", "userId", "proctoringOptionProfileId", "accommodationNotes", "createdAt", "updatedAt"]);
var LmsTokenScalarFieldEnumSchema = z30.enum(["id", "instituteUserId", "accessToken", "refreshToken", "exp", "createdAt", "updatedAt", "isDeleted"]);
var ProctoringOptionScalarFieldEnumSchema = z30.enum(["id", "videoRecordingMode", "secondaryVideoRecordingMode", "audioRecordingMode", "recordScreen", "scanRoom", "recordWebTraffic", "detectSidePanel", "oneScreenEnforcementMode", "disableNewTab", "closeOpenTabs", "tabWhiteList", "disableClipboardMode", "enableGuidedAccessMode", "restrictHandoffMode", "showRecordingPanel", "idVerification", "idVerificationEnableAi", "faceTrackingMode", "objectDetectionMode", "enforceLockdown", "enforceMobile", "isExtension", "isLockdown", "isMobile", "isChromeOs", "isBrowser", "recordClipboard", "recordInput", "excludedWebTrafficRecordingUrls", "disableSpellcheck", "disablePrinting", "disableRightClick", "extensionBlockMode", "allowedExtensionsList", "logInstalledExtensions", "tabBlockList", "hideOtherWindows", "keyboardShortcutBlockList", "disableDownloadFiles", "applicationBlockMode", "blockWindowedApplications", "applicationBlockList", "allowedApplicationsList", "blockVirtualMachines", "recordApplicationsActivity", "logRunningApplications", "quietEnvironment", "enableCalculator", "enableWhiteboard", "enableCodeEditor", "codeEditorRoomId", "uploadScratchNotes", "idVerificationMode", "restrictDevices", "enableManualEndProctoring", "enableReferenceMaterial", "referenceMaterialMode", "audioTranscription", "flaggedKeywords", "uploadSignature", "enableGeofence", "geofenceLocations", "disableVirtualCamera", "enableIpAddressRestriction", "allowedIpAddresses", "createdAt", "updatedAt"]);
var ThirdPartyAssessmentOptionScalarFieldEnumSchema = z30.enum(["id", "thirdPartyUrl", "password", "proctoringAssessmentConfigurationId", "thirdPartyPlatformId", "createdAt", "updatedAt"]);
var ProctoringAssessmentConfigurationScalarFieldEnumSchema = z30.enum(["id", "lmsQuizId", "proctoringOptionProfileId", "enabled", "isLiveProctored", "isLivePopIn", "enablePostReview", "forceChromeOsManagedGuestSession", "password", "createdAt", "updatedAt"]);
var ProctoringOptionProfileScalarFieldEnumSchema = z30.enum(["id", "name", "proctoringOptionId", "type", "createdAt", "updatedAt"]);
var ProctoringOptionInstituteProfileScalarFieldEnumSchema = z30.enum(["id", "enforced", "instituteId", "proctoringOptionProfileId"]);
var ProctoringOptionCourseProfileScalarFieldEnumSchema = z30.enum(["id", "courseId", "proctoringOptionProfileId"]);
var ProctoringOptionUserProfileScalarFieldEnumSchema = z30.enum(["id", "userId", "proctoringOptionProfileId"]);
var ProctoringOptionSubAccountProfileScalarFieldEnumSchema = z30.enum(["id", "proctoringOptionProfileId", "subAccountId"]);
var VerityInstituteSettingsScalarFieldEnumSchema = z30.enum(["id", "instituteId", "allowLiveProctoring", "liveProctoringResetableCredits", "liveProctoringCreditsResetDate", "liveProctoringOneTimeCredits", "allowLivePopIn", "livePopInResetableCredits", "livePopInCreditsResetDate", "livePopInOneTimeCredits", "allowLivePopInPostReview", "allowAutomatedProctoring", "allowStandaloneMode", "disableProctorMode", "allowDemoMode", "allowAiStrictMode", "allowExtensionPlatform", "allowLockdownPlatform", "allowMobilePlatform", "allowBrowserPlatform", "showAllAssessments", "liteMode", "liteModeViewReports", "salesforceId", "cleanupEnabled", "cutoffDate", "cleanupFrequency", "lastCleanupAt", "allowVerityAssessment", "tier", "disabledProctoringOptions", "createdAt", "updatedAt"]);
var InstituteUsageLimitScalarFieldEnumSchema = z30.enum(["id", "instituteId", "usageMode", "category", "proctorGroupId", "type", "resetableLimit", "resetDate", "oneTimeCredits", "annualType", "oneTimeCreditsType"]);
var InstituteUsageCacheScalarFieldEnumSchema = z30.enum(["id", "instituteId", "category", "proctorGroupId", "type", "date", "usage"]);
var InstituteUsageUserCacheScalarFieldEnumSchema = z30.enum(["id", "instituteId", "category", "proctorGroupId", "date", "studentId"]);
var LiveSessionUsageCacheScalarFieldEnumSchema = z30.enum(["id", "instituteId", "proctorGroupIds", "startTime", "endTime", "hours", "sessions", "user"]);
var ProctoringSessionScalarFieldEnumSchema = z30.enum(["id", "uuid", "createdAt", "updatedAt", "lmsQuizId", "studentId", "screencaptureAvailability", "webcamAvailability", "screencaptureVideos", "webcamVideos", "idPhotoAvailable", "personPhotoAvailable", "liveSessionId", "liveUserSessionId", "postReviewPending", "liveProctorSummaryPending", "usageTracked", "ipAddress", "isWebcamAndScreenCaptureDeleted", "webcamAndScreenCaptureDeleteCause", "webcamAndScreenCaptureDeleteAt", "isRoomScanDeleted", "roomScanDeleteCause", "roomScanDeleteAt", "syncedToLmsAt", "signatureType"]);
var DemoSessionScalarFieldEnumSchema = z30.enum(["id", "email", "firstName", "lastName", "organization", "role", "verificationTokenHash", "verificationExpiresAt", "verifiedAt", "demoSessionTokenHash", "demoUserId", "accessControlAccountId", "principalExpiresAt", "proctoringSessionUuid", "completedAt", "resultStatus", "processingStartedAt", "processingDeadlineAt", "resultReadyAt", "resultTokenHash", "resultEmailTokenCiphertext", "resultExpiresAt", "resultEmailStatus", "resultEmailAttempts", "resultEmailLastAttemptAt", "resultEmailNextAttemptAt", "resultEmailSentAt", "cameraDisconnectSelfReportedAt", "createdAt", "updatedAt"]);
var ProctoringSessionAnomaliesScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "type", "flagLevel", "timeStart", "timeEnd", "resolved", "comment", "createdAt", "updatedAt"]);
var ProctoringSessionEventScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "type", "name", "details", "time", "endTime", "createdAt", "updatedAt", "anomalyRef"]);
var ProctoringSessionRekognitionEventScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "time", "storage"]);
var UserScalarFieldEnumSchema = z30.enum(["id", "lmsProvidedUserId", "instituteId", "editInstitute", "name", "userInfo", "other", "accessControlUuid", "stripeUserId", "createdAt", "updatedAt"]);
var UserSessionScalarFieldEnumSchema = z30.enum(["id", "sid", "expires", "data", "createdAt", "updatedAt", "userId"]);
var UserSecurityPinScalarFieldEnumSchema = z30.enum(["userId", "pin", "createdAt", "updatedAt"]);
var UserStateScalarFieldEnumSchema = z30.enum(["userId", "npsSurveyShownAt", "latestReportShownAt", "candidateWelcomeShownAt", "loginCount", "activeSetupStep", "tosAgreedAt", "createdAt", "updatedAt"]);
var YujaAccountScalarFieldEnumSchema = z30.enum(["id", "username", "password", "roleId", "createdAt", "updatedAt"]);
var YujaAccountRoleScalarFieldEnumSchema = z30.enum(["id", "role", "permissions", "createdAt", "updatedAt"]);
var YujaDebugInstanceScalarFieldEnumSchema = z30.enum(["id", "uuid", "system", "createdAt", "updatedAt"]);
var YujaDebugStatisticScalarFieldEnumSchema = z30.enum(["id", "instanceId", "userId", "userSessionId", "proctoringSessionId", "type", "time", "details", "createdAt", "updatedAt"]);
var InstallationScalarFieldEnumSchema = z30.enum(["id", "platform", "version", "fingerprint", "details", "createdAt", "updatedAt"]);
var AuditLogSessionScalarFieldEnumSchema = z30.enum(["id", "installationId", "userId", "fingerprint", "platform", "platformDetails", "systemDetails", "createdAt"]);
var AuditLogScalarFieldEnumSchema = z30.enum(["id", "sessionId", "context", "type", "ipAddress", "proctoringSessionId", "liveSessionId", "details", "time", "createdAt"]);
var LiveSessionScalarFieldEnumSchema = z30.enum(["id", "assessmentId", "liveProvidedSessionId", "status", "name", "startTime", "endTime", "capacity", "demoMode", "createdAt", "updatedAt"]);
var LiveSessionStudentScalarFieldEnumSchema = z30.enum(["id", "liveSessionId", "instituteUserId", "joined", "createdAt", "updatedAt"]);
var ProctorGroupScalarFieldEnumSchema = z30.enum(["id", "name", "ownerInstituteId", "createdAt", "updatedAt"]);
var ProctorGroupAllowedInstituteScalarFieldEnumSchema = z30.enum(["id", "proctorGroupId", "instituteId", "minHoursNotice", "hoursNotAcceptedNotify", "hoursTrackCancellation", "trackCancellationMessage", "createdAt", "updatedAt"]);
var ProctorGroupProctorScalarFieldEnumSchema = z30.enum(["id", "userId", "proctorGroupId", "createdAt", "updatedAt"]);
var ProctorGroupAdminScalarFieldEnumSchema = z30.enum(["id", "userId", "proctorGroupId", "createdAt", "updatedAt"]);
var ProctorAvailabilityScalarFieldEnumSchema = z30.enum(["id", "userId", "availability", "createdAt", "updatedAt"]);
var LiveSessionProctorScalarFieldEnumSchema = z30.enum(["id", "liveSessionId", "instituteUserId", "joined", "createdAt", "updatedAt"]);
var LiveSessionProctorGroupScalarFieldEnumSchema = z30.enum(["id", "liveSessionId", "proctorGroupId", "selectedProctorId", "joined", "checkProctorAt", "createdAt", "updatedAt"]);
var LiveSessionNoteScalarFieldEnumSchema = z30.enum(["id", "liveSessionId", "studentInstituteUserId", "createdAt", "updatedAt"]);
var LiveSessionNoteEditScalarFieldEnumSchema = z30.enum(["id", "liveSessionId", "liveSessionNoteId", "editorId", "editorInstituteUserId", "content", "createdAt", "updatedAt"]);
var CancelledLiveSessionScalarFieldEnumSchema = z30.enum(["id", "liveSessionId", "assessmentId", "assessmentName", "courseId", "courseName", "instituteId", "instituteName", "name", "startTime", "endTime", "capacity", "sessionCreatedAt", "sessionUpdatedAt", "cancelledAt", "recordUpdatedAt"]);
var CancelledLiveSessionProctorGroupScalarFieldEnumSchema = z30.enum(["id", "cancelledLiveSessionId", "proctorGroupId", "selectedProctorId", "createdAt", "updatedAt"]);
var CourseEmailSettingsScalarFieldEnumSchema = z30.enum(["id", "instituteUserId", "courseId", "createdAt", "updatedAt"]);
var CourseSettingsScalarFieldEnumSchema = z30.enum(["id", "courseId", "allowStopRecording", "geofenceLocations", "createdAt", "updatedAt"]);
var ThirdPartyPlatformScalarFieldEnumSchema = z30.enum(["id", "name"]);
var InstituteAndThirdPartyPlatformsScalarFieldEnumSchema = z30.enum(["id", "instituteId", "platformId"]);
var InstituteSettingsScalarFieldEnumSchema = z30.enum(["id", "instituteId", "allowStopRecording", "policyStatementFileName", "defaultProctoringOptionProfileId", "disabledProctoringOptions", "allowStrictMode", "suffix", "createdAt", "updatedAt", "scopeSettingsMode", "lockdownRequireManagedGuestSession", "managedGuestSessionRequireIncognito", "delayRecordingUntilAssessmentStart", "allowPortalMadeThirdPartyAssessments", "flaggedKeywords", "allowQuizAttemptOnChrome", "allowQuizAttemptOnFirefox", "allowQuizAttemptOnSafari", "recordingMaxAgeMonths", "sessionRecordingLimitHours", "proctoringRegistrationNotifications", "preferredProctoringSetupUiVersion", "dailyDigestEmailEnabled", "platformChatEnabled"]);
var UserSettingsScalarFieldEnumSchema = z30.enum(["id", "instituteUserId", "language", "createdAt", "updatedAt"]);
var TemporaryAccessCodeScalarFieldEnumSchema = z30.enum(["accessCode", "userId", "data", "createdAt"]);
var StandaloneStudentInfoScalarFieldEnumSchema = z30.enum(["id", "userId", "accessCode", "email", "firstName", "lastName", "data", "createdAt", "expiryDate", "lmsQuizId", "accountId"]);
var LmsCourseAnomalyConfigScalarFieldEnumSchema = z30.enum(["id", "lmsCourseId", "setting", "createdAt", "updatedAt"]);
var ProctoringSessionStateScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "status", "createdAt", "updatedAt"]);
var ProctoringSessionNoteScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "content", "timestamp", "createdAt", "updatedAt"]);
var ProctoringRoomScanNoteScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "content", "timestamp", "createdAt", "updatedAt"]);
var ExtensionInstallationScalarFieldEnumSchema = z30.enum(["id", "instanceId", "userId", "isUpdate", "time", "details", "createdAt", "updatedAt"]);
var BlackboardAuthorizationRequestScalarFieldEnumSchema = z30.enum(["id", "accountId", "refreshToken", "lmsOrigin", "redirectUri", "codeVerifier", "state", "createdDate", "expiryDate"]);
var InstituteUpdateLogScalarFieldEnumSchema = z30.enum(["id", "accountId", "userId", "details", "timestamp"]);
var ApplicationsScalarFieldEnumSchema = z30.enum(["id", "name", "processName", "iconSvg", "type", "createdAt", "updatedAt"]);
var ExtensionsScalarFieldEnumSchema = z30.enum(["id", "extensionId", "extensionName", "iconSvg", "type", "createdAt", "updatedAt"]);
var SubAccountsScalarFieldEnumSchema = z30.enum(["id", "accountName", "instituteId", "lmsAccountId", "lmsAccountUuid", "parentLmsAccountId", "rootLmsAccountId", "lmsAccountState", "verityEnabled", "createdAt", "updatedAt"]);
var ProctoringSessionReportScalarFieldEnumSchema = z30.enum(["proctoringSessionId", "studentId", "studentName", "quizId", "quizName", "lmsCourseId", "instituteId", "type", "noneFlagCount", "minorFlagCount", "majorFlagCount", "severeFlagCount", "secondsProctored", "sessionStartTime", "anomalyTypeCount", "createdAt", "updatedAt"]);
var InstituteReportScalarFieldEnumSchema = z30.enum(["id", "instituteId", "startDate", "endDate", "uniqueStudents", "uniqueAssessments", "totalSessions", "totalLiveSessions", "totalLiveCheckInSessions", "totalProctoringHours", "totalLiveHours", "averageIntegrityScore", "anomalyTypeCount", "courseTableData", "createdAt", "updatedAt"]);
var InstituteReportSummaryScalarFieldEnumSchema = z30.enum(["id", "instituteId", "startDate", "endDate", "summary", "createdAt", "updatedAt"]);
var InstitutionReportEmailScalarFieldEnumSchema = z30.enum(["id", "institutionId", "month", "year", "adminEmails", "topMetrics", "title", "summary", "isApproved", "isSent", "createdAt", "updatedAt"]);
var ExemptedInstitutionReportEmailUsersScalarFieldEnumSchema = z30.enum(["id", "userId", "createdAt", "updatedAt"]);
var SubAccountReportScalarFieldEnumSchema = z30.enum(["id", "subAccountId", "instituteId", "startDate", "endDate", "uniqueStudents", "uniqueAssessments", "totalSessions", "totalLiveSessions", "totalLiveCheckInSessions", "totalProctoringHours", "totalLiveHours", "averageIntegrityScore", "anomalyTypeCount", "courseTableData", "createdAt", "updatedAt"]);
var SubAccountReportSummaryScalarFieldEnumSchema = z30.enum(["id", "subAccountId", "startDate", "endDate", "summary", "createdAt", "updatedAt"]);
var CourseReportScalarFieldEnumSchema = z30.enum(["id", "courseId", "startDate", "endDate", "uniqueStudents", "uniqueAssessments", "totalSessions", "totalLiveSessions", "totalLiveCheckInSessions", "totalProctoringHours", "totalLiveHours", "averageIntegrityScore", "anomalyTypeCount", "assessmentTableData", "createdAt", "updatedAt"]);
var CourseReportSummaryScalarFieldEnumSchema = z30.enum(["id", "courseId", "startDate", "endDate", "summary", "createdAt", "updatedAt"]);
var MoodleAccessTokenScalarFieldEnumSchema = z30.enum(["id", "instituteId", "accessToken"]);
var SocketSessionScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionUuid", "createdAt"]);
var SocketEventScalarFieldEnumSchema = z30.enum(["id", "socketSessionId", "name", "createdAt", "ackedAt"]);
var LmsTokenAuthorizationRequestScalarFieldEnumSchema = z30.enum(["id", "state", "userId", "courseId", "callbackUrl", "createdAt"]);
var ScratchNoteScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionUuid", "mimeType", "createdAt", "uploadedAt"]);
var ApiRouteConfigScalarFieldEnumSchema = z30.enum(["id", "version", "route", "instituteId", "active", "createdAt", "updatedAt"]);
var RecordingScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "recordingId", "sourceIndex", "type", "chunked", "createdAt", "updatedAt"]);
var RecordingConcatenationRequestScalarFieldEnumSchema = z30.enum(["proctoringSessionId", "type", "number", "requestId", "status", "createdAt", "updatedAt"]);
var RuleScalarFieldEnumSchema = z30.enum(["id", "instituteId", "scope", "scopeValue", "type", "details", "order", "enabled", "createdAt", "updatedAt"]);
var TemporaryEnabledCourseScalarFieldEnumSchema = z30.enum(["id", "courseId", "instituteId"]);
var ProctoringSessionDataDumpScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "source", "mimeType", "createdAt", "uploadedAt"]);
var InstitutionPaymentOptionsScalarFieldEnumSchema = z30.enum(["id", "currency", "instituteId", "automatedType", "liveProctoringType", "liveCheckInType", "livePostReviewType", "livePostReviewPrice", "liveCheckInPrice", "liveProctoringPrice", "automatedProctoringPrice", "enabled", "createdAt", "updatedAt"]);
var StripePaymentSessionScalarFieldEnumSchema = z30.enum(["id", "assessmentId", "userId", "proctoringSessionId", "stripeSessionId", "paid", "claimed", "createdAt", "updatedAt"]);
var RestrictedDevicePeerScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "role", "platform", "options"]);
var RestrictedDeviceStateScalarFieldEnumSchema = z30.enum(["id", "peerId", "connected", "data", "createdAt", "updatedAt"]);
var RestrictedDeviceSingleUseGrantScalarFieldEnumSchema = z30.enum(["id", "peerId", "secret", "accountId", "createdAt"]);
var FeedbackScalarFieldEnumSchema = z30.enum(["id", "userId", "satisfaction", "category", "summary", "details", "status", "isDeleted", "createdAt", "updatedAt"]);
var EventBannerScalarFieldEnumSchema = z30.enum(["id", "createdBy", "title", "link", "buttonText", "institutions", "roles", "startDate", "endDate", "isDeleted", "createdAt", "updatedAt"]);
var InferenceTokenScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "data", "createdAt", "updatedAt"]);
var InstituteStateScalarFieldEnumSchema = z30.enum(["instituteId", "lastVisualizerLaunchTime", "lastLti1p3LaunchTime", "ltiQuickLinkTemplate", "ltiQuickLinkTemplateAt", "createdAt", "updatedAt"]);
var InstituteSetupGuideScalarFieldEnumSchema = z30.enum(["instituteId", "setupProfileId", "setupQuizId", "takeStepUserId", "lti1p3SetupComplete", "visualizerSetupComplete", "apiSetupComplete", "profileSetupComplete", "extensionSetupComplete", "createSetupComplete", "takeSetupComplete", "resultSetupComplete", "createdAt", "updatedAt"]);
var ProctoringSessionSummaryScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "source", "summary", "authorId", "createdAt", "updatedAt"]);
var SetupSupportChatMessageScalarFieldEnumSchema = z30.enum(["id", "proctoringSessionId", "fromUser", "timestamp", "message"]);
var CandidateInterviewOptionsScalarFieldEnumSchema = z30.enum(["id", "title", "candidateName", "candidateEmail", "positionName", "videoPlatform", "meetingLink", "notes", "proctoringAssessmentConfigurationId"]);
var CronExpireRecordingScalarFieldEnumSchema = z30.enum(["id", "instituteId", "lastProcessedId", "startedAt", "updatedAt", "stoppedAt"]);
var QuizQuestionScalarFieldEnumSchema = z30.enum(["id", "quizId", "questionText", "questionOrder", "questionType", "correctionPoints", "options", "configuration", "isDeleted", "aiGradingEnabled", "aiGradingCriteria", "createdAt", "updatedAt"]);
var QuizResponseScalarFieldEnumSchema = z30.enum(["id", "sessionId", "questionSnapshot", "questionNo", "answer", "isAnswered", "createdAt", "updatedAt"]);
var QuizResponseGradeScalarFieldEnumSchema = z30.enum(["id", "responseId", "score", "notes", "isDraft", "isGraded", "gradedByAi", "aiJustification", "createdAt", "updatedAt"]);
var FingerprintsScalarFieldEnumSchema = z30.enum(["id", "fingerprint", "questionId", "responseId", "wordOffset", "createdAt", "updatedAt"]);
var SortOrderSchema = z30.enum(["asc", "desc"]);
var JsonNullValueInputSchema = z30.enum(["JsonNull"]);
var NullableJsonNullValueInputSchema = z30.enum(["DbNull", "JsonNull"]);
var JsonNullValueFilterSchema = z30.enum(["DbNull", "JsonNull", "AnyNull"]);
var QueryModeSchema = z30.enum(["default", "insensitive"]);
var GlobalSettingOrderByRelevanceFieldEnumSchema = z30.enum(["key"]);
var ResourceOrderByRelevanceFieldEnumSchema = z30.enum(["name"]);
var RoleOrderByRelevanceFieldEnumSchema = z30.enum(["name"]);
var NullsOrderSchema = z30.enum(["first", "last"]);
var LmsCourseOrderByRelevanceFieldEnumSchema = z30.enum(["courseId", "accessControlUuid"]);
var LmsQuizOrderByRelevanceFieldEnumSchema = z30.enum(["quizId"]);
var VerityAssessmentOrderByRelevanceFieldEnumSchema = z30.enum(["description", "moduleId"]);
var AssessmentQuestionOrderByRelevanceFieldEnumSchema = z30.enum(["lmsProvidedQuestionId", "title", "bodyHtml", "bodyText", "decoySiteHostname", "decoySitePostId"]);
var QuestionSourcesOrderByRelevanceFieldEnumSchema = z30.enum(["hostname", "email"]);
var LmsQuizCacheOrderByRelevanceFieldEnumSchema = z30.enum(["quizId", "activityUrl"]);
var LmsQuizCopyCheckedOrderByRelevanceFieldEnumSchema = z30.enum(["lmsCourseId", "quizId"]);
var Lti1p3InstituteOrderByRelevanceFieldEnumSchema = z30.enum(["instituteName", "lms", "language", "origin", "ltiClientId", "lmsApiClientId", "lmsApiClientSecret", "deploymentId", "accessControlUuid"]);
var InstitutePendingDeletionOrderByRelevanceFieldEnumSchema = z30.enum(["initiatingUserId", "initiatingUserName", "initiatingUserEmail"]);
var LuminaPartnerKeyOrderByRelevanceFieldEnumSchema = z30.enum(["keyId", "keyHash", "label"]);
var LuminaResultsTokenOrderByRelevanceFieldEnumSchema = z30.enum(["tokenId"]);
var LtiDeploymentHostnameOrderByRelevanceFieldEnumSchema = z30.enum(["hostname"]);
var Lti1p3IssOrderByRelevanceFieldEnumSchema = z30.enum(["iss", "platformOidcEndpoint", "platformJwksEndpoint"]);
var ProctoringExemptionOrderByRelevanceFieldEnumSchema = z30.enum(["accommodationNotes"]);
var InstitutionProctoringAccommodationOrderByRelevanceFieldEnumSchema = z30.enum(["accommodationNotes"]);
var LmsTokenOrderByRelevanceFieldEnumSchema = z30.enum(["accessToken", "refreshToken"]);
var ThirdPartyAssessmentOptionOrderByRelevanceFieldEnumSchema = z30.enum(["thirdPartyUrl", "password"]);
var ProctoringAssessmentConfigurationOrderByRelevanceFieldEnumSchema = z30.enum(["password"]);
var ProctoringOptionProfileOrderByRelevanceFieldEnumSchema = z30.enum(["name"]);
var VerityInstituteSettingsOrderByRelevanceFieldEnumSchema = z30.enum(["salesforceId"]);
var InstituteUsageLimitOrderByRelevanceFieldEnumSchema = z30.enum(["proctorGroupId"]);
var InstituteUsageCacheOrderByRelevanceFieldEnumSchema = z30.enum(["proctorGroupId"]);
var InstituteUsageUserCacheOrderByRelevanceFieldEnumSchema = z30.enum(["proctorGroupId"]);
var LiveSessionUsageCacheOrderByRelevanceFieldEnumSchema = z30.enum(["id"]);
var ProctoringSessionOrderByRelevanceFieldEnumSchema = z30.enum(["uuid", "liveSessionId", "liveUserSessionId", "ipAddress", "webcamAndScreenCaptureDeleteCause", "roomScanDeleteCause"]);
var DemoSessionOrderByRelevanceFieldEnumSchema = z30.enum(["id", "email", "firstName", "lastName", "organization", "role", "verificationTokenHash", "demoSessionTokenHash", "accessControlAccountId", "proctoringSessionUuid", "resultTokenHash", "resultEmailTokenCiphertext"]);
var ProctoringSessionAnomaliesOrderByRelevanceFieldEnumSchema = z30.enum(["type", "comment"]);
var ProctoringSessionEventOrderByRelevanceFieldEnumSchema = z30.enum(["type", "name"]);
var UserOrderByRelevanceFieldEnumSchema = z30.enum(["lmsProvidedUserId", "name", "accessControlUuid", "stripeUserId"]);
var UserSessionOrderByRelevanceFieldEnumSchema = z30.enum(["sid"]);
var UserStateOrderByRelevanceFieldEnumSchema = z30.enum(["activeSetupStep"]);
var YujaAccountOrderByRelevanceFieldEnumSchema = z30.enum(["username", "password"]);
var YujaAccountRoleOrderByRelevanceFieldEnumSchema = z30.enum(["role"]);
var YujaDebugInstanceOrderByRelevanceFieldEnumSchema = z30.enum(["uuid"]);
var YujaDebugStatisticOrderByRelevanceFieldEnumSchema = z30.enum(["type"]);
var InstallationOrderByRelevanceFieldEnumSchema = z30.enum(["id", "version", "fingerprint"]);
var AuditLogSessionOrderByRelevanceFieldEnumSchema = z30.enum(["installationId", "userId", "fingerprint"]);
var AuditLogOrderByRelevanceFieldEnumSchema = z30.enum(["type", "ipAddress", "proctoringSessionId", "liveSessionId"]);
var LiveSessionOrderByRelevanceFieldEnumSchema = z30.enum(["liveProvidedSessionId", "name"]);
var ProctorGroupOrderByRelevanceFieldEnumSchema = z30.enum(["name"]);
var ProctorGroupAllowedInstituteOrderByRelevanceFieldEnumSchema = z30.enum(["trackCancellationMessage"]);
var ProctorGroupProctorOrderByRelevanceFieldEnumSchema = z30.enum(["userId"]);
var ProctorGroupAdminOrderByRelevanceFieldEnumSchema = z30.enum(["userId"]);
var ProctorAvailabilityOrderByRelevanceFieldEnumSchema = z30.enum(["userId"]);
var LiveSessionProctorGroupOrderByRelevanceFieldEnumSchema = z30.enum(["selectedProctorId"]);
var LiveSessionNoteEditOrderByRelevanceFieldEnumSchema = z30.enum(["editorId", "content"]);
var CancelledLiveSessionOrderByRelevanceFieldEnumSchema = z30.enum(["assessmentName", "courseName", "instituteName", "name"]);
var CancelledLiveSessionProctorGroupOrderByRelevanceFieldEnumSchema = z30.enum(["selectedProctorId"]);
var ThirdPartyPlatformOrderByRelevanceFieldEnumSchema = z30.enum(["name"]);
var InstituteSettingsOrderByRelevanceFieldEnumSchema = z30.enum(["policyStatementFileName", "suffix"]);
var TemporaryAccessCodeOrderByRelevanceFieldEnumSchema = z30.enum(["accessCode"]);
var StandaloneStudentInfoOrderByRelevanceFieldEnumSchema = z30.enum(["accessCode", "email", "firstName", "lastName", "accountId"]);
var ProctoringSessionStateOrderByRelevanceFieldEnumSchema = z30.enum(["status"]);
var ProctoringSessionNoteOrderByRelevanceFieldEnumSchema = z30.enum(["content"]);
var ProctoringRoomScanNoteOrderByRelevanceFieldEnumSchema = z30.enum(["content"]);
var BlackboardAuthorizationRequestOrderByRelevanceFieldEnumSchema = z30.enum(["accountId", "refreshToken", "lmsOrigin", "redirectUri", "codeVerifier", "state"]);
var InstituteUpdateLogOrderByRelevanceFieldEnumSchema = z30.enum(["accountId", "userId"]);
var ApplicationsOrderByRelevanceFieldEnumSchema = z30.enum(["name", "iconSvg", "type"]);
var ExtensionsOrderByRelevanceFieldEnumSchema = z30.enum(["extensionId", "extensionName", "iconSvg", "type"]);
var SubAccountsOrderByRelevanceFieldEnumSchema = z30.enum(["accountName", "lmsAccountId", "lmsAccountUuid", "parentLmsAccountId", "rootLmsAccountId", "lmsAccountState"]);
var ProctoringSessionReportOrderByRelevanceFieldEnumSchema = z30.enum(["studentName", "quizName"]);
var InstitutionReportEmailOrderByRelevanceFieldEnumSchema = z30.enum(["title", "summary"]);
var MoodleAccessTokenOrderByRelevanceFieldEnumSchema = z30.enum(["accessToken"]);
var SocketSessionOrderByRelevanceFieldEnumSchema = z30.enum(["proctoringSessionUuid"]);
var SocketEventOrderByRelevanceFieldEnumSchema = z30.enum(["name"]);
var LmsTokenAuthorizationRequestOrderByRelevanceFieldEnumSchema = z30.enum(["state", "callbackUrl"]);
var ScratchNoteOrderByRelevanceFieldEnumSchema = z30.enum(["id", "proctoringSessionUuid"]);
var ApiRouteConfigOrderByRelevanceFieldEnumSchema = z30.enum(["route"]);
var RecordingOrderByRelevanceFieldEnumSchema = z30.enum(["recordingId"]);
var RecordingConcatenationRequestOrderByRelevanceFieldEnumSchema = z30.enum(["type", "requestId"]);
var RuleOrderByRelevanceFieldEnumSchema = z30.enum(["scopeValue"]);
var TemporaryEnabledCourseOrderByRelevanceFieldEnumSchema = z30.enum(["courseId"]);
var ProctoringSessionDataDumpOrderByRelevanceFieldEnumSchema = z30.enum(["id", "source", "mimeType"]);
var InstitutionPaymentOptionsOrderByRelevanceFieldEnumSchema = z30.enum(["currency"]);
var StripePaymentSessionOrderByRelevanceFieldEnumSchema = z30.enum(["stripeSessionId"]);
var RestrictedDevicePeerOrderByRelevanceFieldEnumSchema = z30.enum(["id"]);
var RestrictedDeviceStateOrderByRelevanceFieldEnumSchema = z30.enum(["peerId"]);
var RestrictedDeviceSingleUseGrantOrderByRelevanceFieldEnumSchema = z30.enum(["peerId", "secret", "accountId"]);
var FeedbackOrderByRelevanceFieldEnumSchema = z30.enum(["summary", "details"]);
var EventBannerOrderByRelevanceFieldEnumSchema = z30.enum(["title", "link", "buttonText"]);
var InferenceTokenOrderByRelevanceFieldEnumSchema = z30.enum(["id", "data"]);
var InstituteStateOrderByRelevanceFieldEnumSchema = z30.enum(["ltiQuickLinkTemplate"]);
var ProctoringSessionSummaryOrderByRelevanceFieldEnumSchema = z30.enum(["authorId"]);
var SetupSupportChatMessageOrderByRelevanceFieldEnumSchema = z30.enum(["message"]);
var CandidateInterviewOptionsOrderByRelevanceFieldEnumSchema = z30.enum(["title", "candidateName", "candidateEmail", "positionName", "meetingLink", "notes"]);
var QuizQuestionOrderByRelevanceFieldEnumSchema = z30.enum(["questionText", "aiGradingCriteria"]);
var QuizResponseGradeOrderByRelevanceFieldEnumSchema = z30.enum(["notes", "aiJustification"]);
var FingerprintsOrderByRelevanceFieldEnumSchema = z30.enum(["fingerprint"]);
var RoleTypeSchema = z30.enum(["assessment", "course", "institute", "verity"]);
var PermissionSchema = z30.enum(["read", "write", "deny"]);
var VerityAssessmentTypeSchema = z30.enum(["quiz", "assignment"]);
var ScoringStrategySchema = z30.enum(["highest", "first", "latest"]);
var InstitutionTypeSchema = z30.enum(["candidate", "verity"]);
var AccountTypeSchema = z30.enum(["production", "test", "trial", "demo"]);
var InstitutePendingDeletionStatusSchema = z30.enum(["pendingFinanceApproval", "pendingArchitectApproval", "pendingDeletion", "deleted"]);
var RecordingModeSchema2 = z30.enum(["off", "normal", "strict"]);
var DisableClipboardModeSchema = z30.enum(["off", "tab", "system"]);
var CalculatorModeSchema = z30.enum(["off", "scientific", "standard"]);
var ReferenceMaterialModeSchema = z30.enum(["off", "instructor", "student"]);
var ProctoringOptionProfileTypeSchema = z30.enum(["institute", "course", "user", "assessment", "preset", "subAccount"]);
var DisableProctorModeSchema = z30.enum(["off", "extension", "lockdown", "mobile"]);
var CleanupFrequencySchema = z30.enum(["daily", "weekly", "monthly"]);
var UsageModeSchema = z30.enum(["unlimited", "limited", "metered"]);
var UsageCategorySchema = z30.enum(["automated", "live", "popIn", "popInPostReview"]);
var UsageTypeSchema = z30.enum(["sessions", "hours", "user"]);
var ProctoringSessionScreencaptureAvailabilitySchema = z30.enum(["unavailable", "processing", "available", "error"]);
var ProctoringSessionWebcamAvailabilitySchema = z30.enum(["unavailable", "processing", "available", "error"]);
var SignatureTypeSchema = z30.enum(["photo", "drawn"]);
var DemoResultStatusSchema = z30.enum(["pending", "processing", "ready", "upload_incomplete"]);
var DemoResultEmailStatusSchema = z30.enum(["pending", "queued", "sending", "sent", "failed"]);
var ProctoringSessionEventStorageSchema = z30.enum(["S3"]);
var InstallationPlatformSchema = z30.enum(["extension", "lockdown", "mobile"]);
var AuditLogPlatformSchema = z30.enum(["extension", "lockdown", "mobile", "web", "plugin"]);
var LiveSessionStatusSchema = z30.enum(["pending", "scheduled", "creating", "created", "destroying", "destroyed", "error"]);
var ScopeSettingsTypeSchema = z30.enum(["allCourses", "selectedAccounts", "selectedCourses"]);
var ProctoringSetupUiVersionSchema = z30.enum(["v1", "v2"]);
var LanguageSchema = z30.enum(["en", "fr"]);
var ProctoringSessionTypeSchema = z30.enum(["automated", "live", "popIn"]);
var ScratchNoteMimeTypeSchema = z30.enum(["jpeg", "png", "jpg"]);
var ApiVersionSchema = z30.enum(["v1", "v2"]);
var RecordingTypeSchema = z30.enum(["webcam", "screencapture", "merged", "roomscan"]);
var ConcatenationRequestStatusSchema = z30.enum(["POSTPROCESS_STARTED", "POSTPROCESS_COMPLETE", "POSTPROCESS_FAILED"]);
var RuleScopeSchema = z30.enum(["global", "lmsProvidedCourseId", "courseNamePrefix"]);
var RuleTypeSchema = z30.enum(["editConfiguration"]);
var PaymentTypeSchema = z30.enum(["assessment", "attempt"]);
var RestrictedDeviceRoleSchema2 = z30.enum(["source", "sink"]);
var RestrictedDevicePlatformSchema2 = z30.enum(["extension", "mobile", "lockdown", "browser"]);
var SatisfactionLevelSchema = z30.enum(["excellent", "good", "poor", "satisfied"]);
var FeedbackCategorySchema = z30.enum(["bug", "comment", "other", "suggestion"]);
var FeedbackStatusSchema = z30.enum(["completed", "inProgress", "notStarted"]);
var ProctoringSessionSummarySourceSchema = z30.enum(["ai", "liveProctor"]);
var VideoPlatformSchema = z30.enum(["zoom", "teams", "googleMeet", "other"]);
var QuestionTypeSchema = z30.enum(["MultipleChoice", "TrueFalse", "FillInTheBlank", "Matching", "FileUpload", "Essay", "Ordering", "AudioRecording", "FileSubmission", "DragAndDrop", "Dropdown", "Spreadsheet", "Coding"]);
var GlobalSettingSchema = z30.object({
  key: z30.string(),
  value: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10")
});
var ResourceSchema = z30.object({
  id: z30.number().int(),
  name: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RoleSchema = z30.object({
  id: z30.number().int(),
  name: z30.string(),
  type: RoleTypeSchema,
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RoleResourcePermissionSchema = z30.object({
  id: z30.number().int(),
  roleId: z30.number().int(),
  resourceId: z30.number().int(),
  permission: PermissionSchema,
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstituteUserRoleSchema = z30.object({
  id: z30.number().int(),
  instituteUserId: z30.number().int(),
  roleId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var CourseUserRoleSchema = z30.object({
  id: z30.number().int(),
  courseUserId: z30.number().int(),
  roleId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LmsCourseSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  courseId: z30.string(),
  courseInfo: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  accessControlUuid: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  verityEnabled: z30.boolean().default(true),
  subAccountId: z30.number().int().nullish()
});
var LmsCourseUserSchema = z30.object({
  id: z30.number().int(),
  userId: z30.number().int(),
  courseId: z30.number().int(),
  viewVideos: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LmsQuizSchema = z30.object({
  id: z30.number().int(),
  courseId: z30.number().int(),
  quizId: z30.string(),
  quizInfo: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  other: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  isDeleted: z30.boolean(),
  summary: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  sourcesLastScanned: z30.date().nullish(),
  ltiPayload: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish()
});
var VerityAssessmentSchema = z30.object({
  quizId: z30.number().int(),
  type: VerityAssessmentTypeSchema,
  isPublished: z30.boolean(),
  syncGradebook: z30.boolean().nullish(),
  startTime: z30.date(),
  endTime: z30.date(),
  allowLateSubmission: z30.boolean(),
  lateSubmissionDate: z30.date().nullish(),
  shuffleQuestions: z30.boolean().nullish(),
  questionsPerPage: z30.number().int().nullish(),
  numAttempts: z30.number().int().default(1),
  showScoreOnSubmit: z30.boolean().nullish(),
  duration: z30.number().int().nullish(),
  description: z30.string().nullish(),
  moduleId: z30.string().nullish(),
  scoringStrategy: ScoringStrategySchema.default("first"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var AssessmentQuestionSchema = z30.object({
  id: z30.number().int(),
  assessmentId: z30.number().int(),
  lmsProvidedQuestionId: z30.string().nullish(),
  title: z30.string().nullish(),
  bodyHtml: z30.string().nullish(),
  bodyText: z30.string().nullish(),
  sources: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  numSources: z30.number().int(),
  decoySiteHostname: z30.string().nullish(),
  decoySitePostId: z30.string().nullish(),
  isDeleted: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var QuestionSourcesSchema = z30.object({
  id: z30.number().int(),
  hostname: z30.string(),
  email: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LmsQuizCacheSchema = z30.object({
  id: z30.number().int(),
  quizId: z30.string(),
  courseId: z30.number().int(),
  platformId: z30.number().int().nullish(),
  activityUrl: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LmsQuizCopyCheckedSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  lmsCourseId: z30.string(),
  quizId: z30.string(),
  createdAt: z30.date()
});
var Lti1p3InstituteSchema = z30.object({
  id: z30.number().int(),
  instituteName: z30.string().nullish(),
  lms: z30.string().nullish(),
  language: z30.string().nullish(),
  origin: z30.string().nullish(),
  issId: z30.number().int().nullish(),
  ltiClientId: z30.string().nullish(),
  lmsApiClientId: z30.string().nullish(),
  lmsApiClientSecret: z30.string().nullish(),
  lmsApiIncludeD2LCorePermission: z30.boolean().default(true),
  deploymentId: z30.string().nullish(),
  accessControlUuid: z30.string().nullish(),
  isDisabled: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  institutionType: InstitutionTypeSchema.default("verity"),
  accountType: AccountTypeSchema.default("production")
});
var InstitutePendingDeletionSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  initiatingUserId: z30.string(),
  initiatingUserName: z30.string(),
  initiatingUserEmail: z30.string(),
  deletionDate: z30.date().nullish(),
  status: InstitutePendingDeletionStatusSchema.default("pendingFinanceApproval"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LuminaPartnerKeySchema = z30.object({
  id: z30.number().int(),
  keyId: z30.string(),
  keyHash: z30.string(),
  label: z30.string(),
  createdAt: z30.date(),
  lastUsedAt: z30.date().nullish(),
  revokedAt: z30.date().nullish()
});
var LuminaResultsTokenSchema = z30.object({
  id: z30.number().int(),
  tokenId: z30.string(),
  instituteId: z30.number().int(),
  courseId: z30.number().int(),
  lmsQuizId: z30.number().int(),
  studentId: z30.number().int(),
  createdAt: z30.date(),
  expiresAt: z30.date(),
  redeemedAt: z30.date().nullish()
});
var LtiDeploymentHostnameSchema = z30.object({
  id: z30.number().int(),
  ltiDeploymentId: z30.number().int(),
  hostname: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var Lti1p3IssSchema = z30.object({
  id: z30.number().int(),
  iss: z30.string(),
  platformOidcEndpoint: z30.string(),
  platformJwksEndpoint: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringExemptionSchema = z30.object({
  id: z30.number().int(),
  assessmentId: z30.number().int(),
  userId: z30.number().int(),
  proctoringOptionProfileId: z30.number().int().nullish(),
  accommodationNotes: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstitutionProctoringAccommodationSchema = z30.object({
  id: z30.number().int(),
  userId: z30.number().int(),
  proctoringOptionProfileId: z30.number().int().nullish(),
  accommodationNotes: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LmsTokenSchema = z30.object({
  id: z30.number().int(),
  instituteUserId: z30.number().int(),
  accessToken: z30.string(),
  refreshToken: z30.string(),
  exp: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  isDeleted: z30.boolean()
});
var ProctoringOptionSchema = z30.object({
  id: z30.number().int(),
  videoRecordingMode: RecordingModeSchema2.default("off"),
  secondaryVideoRecordingMode: RecordingModeSchema2.default("off"),
  audioRecordingMode: RecordingModeSchema2.default("off"),
  recordScreen: z30.boolean().default(true),
  scanRoom: z30.boolean(),
  recordWebTraffic: z30.boolean(),
  detectSidePanel: z30.boolean(),
  oneScreenEnforcementMode: RecordingModeSchema2.default("off"),
  disableNewTab: z30.boolean(),
  closeOpenTabs: z30.boolean(),
  tabWhiteList: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  disableClipboardMode: DisableClipboardModeSchema.default("off"),
  enableGuidedAccessMode: z30.boolean(),
  restrictHandoffMode: RecordingModeSchema2.default("off"),
  showRecordingPanel: z30.boolean().default(true),
  idVerification: z30.boolean(),
  idVerificationEnableAi: z30.boolean(),
  faceTrackingMode: RecordingModeSchema2.default("off"),
  objectDetectionMode: RecordingModeSchema2.default("off"),
  enforceLockdown: z30.boolean(),
  enforceMobile: z30.boolean(),
  isExtension: z30.boolean(),
  isLockdown: z30.boolean(),
  isMobile: z30.boolean(),
  isChromeOs: z30.boolean(),
  isBrowser: z30.boolean(),
  recordClipboard: z30.boolean(),
  recordInput: z30.boolean(),
  excludedWebTrafficRecordingUrls: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  disableSpellcheck: z30.boolean(),
  disablePrinting: z30.boolean(),
  disableRightClick: z30.boolean(),
  extensionBlockMode: RecordingModeSchema2.default("off"),
  allowedExtensionsList: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  logInstalledExtensions: z30.boolean(),
  tabBlockList: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  hideOtherWindows: z30.boolean().default(true),
  keyboardShortcutBlockList: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  disableDownloadFiles: z30.boolean(),
  applicationBlockMode: RecordingModeSchema2.default("off"),
  blockWindowedApplications: z30.boolean(),
  applicationBlockList: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  allowedApplicationsList: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  blockVirtualMachines: z30.boolean(),
  recordApplicationsActivity: z30.boolean(),
  logRunningApplications: z30.boolean(),
  quietEnvironment: z30.boolean(),
  enableCalculator: CalculatorModeSchema.default("off"),
  enableWhiteboard: z30.boolean(),
  enableCodeEditor: z30.boolean(),
  codeEditorRoomId: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  uploadScratchNotes: z30.boolean(),
  idVerificationMode: RecordingModeSchema2.default("off"),
  restrictDevices: z30.boolean(),
  enableManualEndProctoring: z30.boolean(),
  enableReferenceMaterial: z30.boolean(),
  referenceMaterialMode: ReferenceMaterialModeSchema.default("off"),
  audioTranscription: z30.boolean(),
  flaggedKeywords: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").default("null"),
  uploadSignature: z30.boolean(),
  enableGeofence: z30.boolean(),
  geofenceLocations: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  disableVirtualCamera: z30.boolean(),
  enableIpAddressRestriction: z30.boolean(),
  allowedIpAddresses: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ThirdPartyAssessmentOptionSchema = z30.object({
  id: z30.number().int(),
  thirdPartyUrl: z30.string().nullish(),
  password: z30.string().nullish(),
  proctoringAssessmentConfigurationId: z30.number().int(),
  thirdPartyPlatformId: z30.number().int().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringAssessmentConfigurationSchema = z30.object({
  id: z30.number().int(),
  lmsQuizId: z30.number().int(),
  proctoringOptionProfileId: z30.number().int(),
  enabled: z30.boolean(),
  isLiveProctored: z30.boolean(),
  isLivePopIn: z30.boolean(),
  enablePostReview: z30.boolean(),
  forceChromeOsManagedGuestSession: z30.boolean(),
  password: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringOptionProfileSchema = z30.object({
  id: z30.number().int(),
  name: z30.string().nullish(),
  proctoringOptionId: z30.number().int(),
  type: ProctoringOptionProfileTypeSchema,
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringOptionInstituteProfileSchema = z30.object({
  id: z30.number().int(),
  enforced: z30.boolean(),
  instituteId: z30.number().int(),
  proctoringOptionProfileId: z30.number().int()
});
var ProctoringOptionCourseProfileSchema = z30.object({
  id: z30.number().int(),
  courseId: z30.number().int(),
  proctoringOptionProfileId: z30.number().int()
});
var ProctoringOptionUserProfileSchema = z30.object({
  id: z30.number().int(),
  userId: z30.number().int(),
  proctoringOptionProfileId: z30.number().int()
});
var ProctoringOptionSubAccountProfileSchema = z30.object({
  id: z30.number().int(),
  proctoringOptionProfileId: z30.number().int(),
  subAccountId: z30.number().int()
});
var VerityInstituteSettingsSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  allowLiveProctoring: z30.boolean(),
  liveProctoringResetableCredits: z30.number().int().nullish(),
  liveProctoringCreditsResetDate: z30.date().nullish(),
  liveProctoringOneTimeCredits: z30.number(),
  allowLivePopIn: z30.boolean(),
  livePopInResetableCredits: z30.number().int().nullish(),
  livePopInCreditsResetDate: z30.date().nullish(),
  livePopInOneTimeCredits: z30.number(),
  allowLivePopInPostReview: z30.boolean(),
  allowAutomatedProctoring: z30.boolean().default(true),
  allowStandaloneMode: z30.boolean(),
  disableProctorMode: DisableProctorModeSchema.default("off"),
  allowDemoMode: z30.boolean(),
  allowAiStrictMode: z30.boolean(),
  allowExtensionPlatform: z30.boolean().default(true),
  allowLockdownPlatform: z30.boolean().default(true),
  allowMobilePlatform: z30.boolean(),
  allowBrowserPlatform: z30.boolean(),
  showAllAssessments: z30.boolean().default(true),
  liteMode: z30.boolean(),
  liteModeViewReports: z30.boolean(),
  salesforceId: z30.string().nullish(),
  cleanupEnabled: z30.boolean(),
  cutoffDate: z30.date().nullish(),
  cleanupFrequency: CleanupFrequencySchema.nullish(),
  lastCleanupAt: z30.date().nullish(),
  allowVerityAssessment: z30.boolean().default(true),
  tier: z30.number().int().default(1),
  disabledProctoringOptions: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstituteUsageLimitSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  usageMode: UsageModeSchema.default("unlimited"),
  category: UsageCategorySchema,
  proctorGroupId: z30.string(),
  type: UsageTypeSchema,
  resetableLimit: z30.number().int(),
  resetDate: z30.date(),
  oneTimeCredits: z30.number(),
  annualType: UsageTypeSchema,
  oneTimeCreditsType: UsageTypeSchema
});
var InstituteUsageCacheSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  category: UsageCategorySchema,
  proctorGroupId: z30.string(),
  type: UsageTypeSchema,
  date: z30.date(),
  usage: z30.number()
});
var InstituteUsageUserCacheSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  category: UsageCategorySchema,
  proctorGroupId: z30.string(),
  date: z30.date(),
  studentId: z30.number().int()
});
var LiveSessionUsageCacheSchema = z30.object({
  id: z30.string(),
  instituteId: z30.number().int(),
  proctorGroupIds: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").default("[]"),
  startTime: z30.date(),
  endTime: z30.date(),
  hours: z30.number(),
  sessions: z30.number(),
  user: z30.number()
});
var ProctoringSessionSchema = z30.object({
  id: z30.number().int(),
  uuid: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  lmsQuizId: z30.number().int(),
  studentId: z30.number().int(),
  screencaptureAvailability: ProctoringSessionScreencaptureAvailabilitySchema.default("unavailable"),
  webcamAvailability: ProctoringSessionWebcamAvailabilitySchema.default("unavailable"),
  screencaptureVideos: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  webcamVideos: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  idPhotoAvailable: z30.boolean(),
  personPhotoAvailable: z30.boolean(),
  liveSessionId: z30.string().nullish(),
  liveUserSessionId: z30.string().nullish(),
  postReviewPending: z30.boolean(),
  liveProctorSummaryPending: z30.boolean(),
  usageTracked: z30.boolean(),
  ipAddress: z30.string().nullish(),
  isWebcamAndScreenCaptureDeleted: z30.boolean(),
  webcamAndScreenCaptureDeleteCause: z30.string().nullish(),
  webcamAndScreenCaptureDeleteAt: z30.date().nullish(),
  isRoomScanDeleted: z30.boolean(),
  roomScanDeleteCause: z30.string().nullish(),
  roomScanDeleteAt: z30.date().nullish(),
  syncedToLmsAt: z30.date().nullish(),
  signatureType: SignatureTypeSchema.nullish()
});
var DemoSessionSchema = z30.object({
  id: z30.string(),
  email: z30.string(),
  firstName: z30.string(),
  lastName: z30.string(),
  organization: z30.string(),
  role: z30.string(),
  verificationTokenHash: z30.string(),
  verificationExpiresAt: z30.date(),
  verifiedAt: z30.date().nullish(),
  demoSessionTokenHash: z30.string().nullish(),
  demoUserId: z30.number().int().nullish(),
  accessControlAccountId: z30.string().nullish(),
  principalExpiresAt: z30.date().nullish(),
  proctoringSessionUuid: z30.string().nullish(),
  completedAt: z30.date().nullish(),
  resultStatus: DemoResultStatusSchema.default("pending"),
  processingStartedAt: z30.date().nullish(),
  processingDeadlineAt: z30.date().nullish(),
  resultReadyAt: z30.date().nullish(),
  resultTokenHash: z30.string().nullish(),
  resultEmailTokenCiphertext: z30.string().nullish(),
  resultExpiresAt: z30.date().nullish(),
  resultEmailStatus: DemoResultEmailStatusSchema.default("pending"),
  resultEmailAttempts: z30.number().int(),
  resultEmailLastAttemptAt: z30.date().nullish(),
  resultEmailNextAttemptAt: z30.date().nullish(),
  resultEmailSentAt: z30.date().nullish(),
  cameraDisconnectSelfReportedAt: z30.date().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringSessionAnomaliesSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  type: z30.string(),
  flagLevel: z30.number().int(),
  timeStart: z30.date().nullish(),
  timeEnd: z30.date().nullish(),
  resolved: z30.boolean(),
  comment: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringSessionEventSchema2 = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  type: z30.string(),
  name: z30.string(),
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  time: z30.date(),
  endTime: z30.date().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  anomalyRef: z30.number().int().nullish()
});
var ProctoringSessionRekognitionEventSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  time: z30.date(),
  storage: ProctoringSessionEventStorageSchema
});
var UserSchema = z30.object({
  id: z30.number().int(),
  lmsProvidedUserId: z30.string(),
  instituteId: z30.number().int(),
  editInstitute: z30.boolean(),
  name: z30.string().nullish(),
  userInfo: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  other: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  accessControlUuid: z30.string().nullish(),
  stripeUserId: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var UserSessionSchema = z30.object({
  id: z30.number().int(),
  sid: z30.string(),
  expires: z30.date().nullish(),
  data: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  userId: z30.number().int().nullish()
});
var UserSecurityPinSchema = z30.object({
  userId: z30.number().int(),
  pin: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var UserStateSchema = z30.object({
  userId: z30.number().int(),
  npsSurveyShownAt: z30.date().nullish(),
  latestReportShownAt: z30.date().nullish(),
  candidateWelcomeShownAt: z30.date().nullish(),
  loginCount: z30.number().int(),
  activeSetupStep: z30.string().nullish(),
  tosAgreedAt: z30.date().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var YujaAccountSchema = z30.object({
  id: z30.number().int(),
  username: z30.string(),
  password: z30.string(),
  roleId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var YujaAccountRoleSchema = z30.object({
  id: z30.number().int(),
  role: z30.string(),
  permissions: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var YujaDebugInstanceSchema = z30.object({
  id: z30.number().int(),
  uuid: z30.string(),
  system: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var YujaDebugStatisticSchema = z30.object({
  id: z30.number().int(),
  instanceId: z30.number().int(),
  userId: z30.number().int().nullish(),
  userSessionId: z30.number().int().nullish(),
  proctoringSessionId: z30.number().int().nullish(),
  type: z30.string(),
  time: z30.date(),
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstallationSchema = z30.object({
  id: z30.string(),
  platform: InstallationPlatformSchema,
  version: z30.string(),
  fingerprint: z30.string(),
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var AuditLogSessionSchema = z30.object({
  id: z30.number().int(),
  installationId: z30.string().nullish(),
  userId: z30.string().nullish(),
  fingerprint: z30.string().nullish(),
  platform: AuditLogPlatformSchema,
  platformDetails: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  systemDetails: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date()
});
var AuditLogSchema = z30.object({
  id: z30.number().int(),
  sessionId: z30.number().int(),
  context: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  type: z30.string(),
  ipAddress: z30.string().nullish(),
  proctoringSessionId: z30.string().nullish(),
  liveSessionId: z30.string().nullish(),
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  time: z30.date(),
  createdAt: z30.date()
});
var LiveSessionSchema = z30.object({
  id: z30.number().int(),
  assessmentId: z30.number().int(),
  liveProvidedSessionId: z30.string().nullish(),
  status: LiveSessionStatusSchema.default("scheduled"),
  name: z30.string(),
  startTime: z30.date(),
  endTime: z30.date(),
  capacity: z30.number().int().default(50),
  demoMode: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LiveSessionStudentSchema = z30.object({
  id: z30.number().int(),
  liveSessionId: z30.number().int(),
  instituteUserId: z30.number().int(),
  joined: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctorGroupSchema = z30.object({
  id: z30.number().int(),
  name: z30.string(),
  ownerInstituteId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctorGroupAllowedInstituteSchema = z30.object({
  id: z30.number().int(),
  proctorGroupId: z30.number().int(),
  instituteId: z30.number().int(),
  minHoursNotice: z30.number().int().default(36),
  hoursNotAcceptedNotify: z30.number().int().default(24),
  hoursTrackCancellation: z30.number().int(),
  trackCancellationMessage: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctorGroupProctorSchema = z30.object({
  id: z30.number().int(),
  userId: z30.string(),
  proctorGroupId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctorGroupAdminSchema = z30.object({
  id: z30.number().int(),
  userId: z30.string(),
  proctorGroupId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctorAvailabilitySchema = z30.object({
  id: z30.number().int(),
  userId: z30.string(),
  availability: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LiveSessionProctorSchema = z30.object({
  id: z30.number().int(),
  liveSessionId: z30.number().int(),
  instituteUserId: z30.number().int(),
  joined: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LiveSessionProctorGroupSchema = z30.object({
  id: z30.number().int(),
  liveSessionId: z30.number().int(),
  proctorGroupId: z30.number().int(),
  selectedProctorId: z30.string().nullish(),
  joined: z30.boolean(),
  checkProctorAt: z30.date().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LiveSessionNoteSchema = z30.object({
  id: z30.number().int(),
  liveSessionId: z30.number().int(),
  studentInstituteUserId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var LiveSessionNoteEditSchema = z30.object({
  id: z30.number().int(),
  liveSessionId: z30.number().int(),
  liveSessionNoteId: z30.number().int(),
  editorId: z30.string(),
  editorInstituteUserId: z30.number().int().nullish(),
  content: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var CancelledLiveSessionSchema = z30.object({
  id: z30.number().int(),
  liveSessionId: z30.number().int(),
  assessmentId: z30.number().int(),
  assessmentName: z30.string().nullish(),
  courseId: z30.number().int(),
  courseName: z30.string().nullish(),
  instituteId: z30.number().int(),
  instituteName: z30.string().nullish(),
  name: z30.string(),
  startTime: z30.date(),
  endTime: z30.date(),
  capacity: z30.number().int().default(50),
  sessionCreatedAt: z30.date(),
  sessionUpdatedAt: z30.date(),
  cancelledAt: z30.date(),
  recordUpdatedAt: z30.date()
});
var CancelledLiveSessionProctorGroupSchema = z30.object({
  id: z30.number().int(),
  cancelledLiveSessionId: z30.number().int(),
  proctorGroupId: z30.number().int(),
  selectedProctorId: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var CourseEmailSettingsSchema = z30.object({
  id: z30.number().int(),
  instituteUserId: z30.number().int(),
  courseId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var CourseSettingsSchema = z30.object({
  id: z30.number().int(),
  courseId: z30.number().int(),
  allowStopRecording: z30.boolean().default(true),
  geofenceLocations: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ThirdPartyPlatformSchema = z30.object({
  id: z30.number().int(),
  name: z30.string()
});
var InstituteAndThirdPartyPlatformsSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  platformId: z30.number().int()
});
var InstituteSettingsSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  allowStopRecording: z30.boolean().default(true),
  policyStatementFileName: z30.string().nullish(),
  defaultProctoringOptionProfileId: z30.number().int().nullish(),
  disabledProctoringOptions: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  allowStrictMode: z30.boolean().default(true),
  suffix: z30.string().default("YuJa Verity Test Proctoring").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date(),
  scopeSettingsMode: ScopeSettingsTypeSchema.default("allCourses"),
  lockdownRequireManagedGuestSession: z30.boolean(),
  managedGuestSessionRequireIncognito: z30.boolean().default(true),
  delayRecordingUntilAssessmentStart: z30.boolean(),
  allowPortalMadeThirdPartyAssessments: z30.boolean(),
  flaggedKeywords: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").default("null"),
  allowQuizAttemptOnChrome: z30.boolean().default(true),
  allowQuizAttemptOnFirefox: z30.boolean(),
  allowQuizAttemptOnSafari: z30.boolean(),
  recordingMaxAgeMonths: z30.number().int().default(12),
  sessionRecordingLimitHours: z30.number().int().default(12).nullish(),
  proctoringRegistrationNotifications: z30.boolean().default(true),
  preferredProctoringSetupUiVersion: ProctoringSetupUiVersionSchema.default("v2"),
  dailyDigestEmailEnabled: z30.boolean().default(true),
  platformChatEnabled: z30.boolean()
});
var UserSettingsSchema = z30.object({
  id: z30.number().int(),
  instituteUserId: z30.number().int(),
  language: LanguageSchema.nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var TemporaryAccessCodeSchema = z30.object({
  accessCode: z30.string(),
  userId: z30.number().int(),
  data: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date()
});
var StandaloneStudentInfoSchema = z30.object({
  id: z30.number().int(),
  userId: z30.number().int(),
  accessCode: z30.string(),
  email: z30.string(),
  firstName: z30.string(),
  lastName: z30.string(),
  data: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  expiryDate: z30.date(),
  lmsQuizId: z30.number().int(),
  accountId: z30.string().nullish()
});
var LmsCourseAnomalyConfigSchema = z30.object({
  id: z30.number().int(),
  lmsCourseId: z30.number().int(),
  setting: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringSessionStateSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  status: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringSessionNoteSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  content: z30.string(),
  timestamp: z30.date(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringRoomScanNoteSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  content: z30.string(),
  timestamp: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ExtensionInstallationSchema = z30.object({
  id: z30.number().int(),
  instanceId: z30.number().int(),
  userId: z30.number().int().nullish(),
  isUpdate: z30.boolean(),
  time: z30.date(),
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var BlackboardAuthorizationRequestSchema = z30.object({
  id: z30.number().int(),
  accountId: z30.string(),
  refreshToken: z30.string(),
  lmsOrigin: z30.string(),
  redirectUri: z30.string(),
  codeVerifier: z30.string(),
  state: z30.string(),
  createdDate: z30.date(),
  expiryDate: z30.date()
});
var InstituteUpdateLogSchema = z30.object({
  id: z30.number().int(),
  accountId: z30.string().nullish(),
  userId: z30.string().nullish(),
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  timestamp: z30.date()
});
var ApplicationsSchema = z30.object({
  id: z30.number().int(),
  name: z30.string(),
  processName: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  iconSvg: z30.string(),
  type: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ExtensionsSchema = z30.object({
  id: z30.number().int(),
  extensionId: z30.string(),
  extensionName: z30.string(),
  iconSvg: z30.string(),
  type: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var SubAccountsSchema = z30.object({
  id: z30.number().int(),
  accountName: z30.string(),
  instituteId: z30.number().int(),
  lmsAccountId: z30.string(),
  lmsAccountUuid: z30.string().nullish(),
  parentLmsAccountId: z30.string().nullish(),
  rootLmsAccountId: z30.string().nullish(),
  lmsAccountState: z30.string().nullish(),
  verityEnabled: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringSessionReportSchema = z30.object({
  proctoringSessionId: z30.number().int(),
  studentId: z30.number().int(),
  studentName: z30.string().nullish(),
  quizId: z30.number().int(),
  quizName: z30.string().nullish(),
  lmsCourseId: z30.number().int(),
  instituteId: z30.number().int(),
  type: ProctoringSessionTypeSchema,
  noneFlagCount: z30.number().int(),
  minorFlagCount: z30.number().int(),
  majorFlagCount: z30.number().int(),
  severeFlagCount: z30.number().int(),
  secondsProctored: z30.number().int(),
  sessionStartTime: z30.date(),
  anomalyTypeCount: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstituteReportSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  startDate: z30.date(),
  endDate: z30.date(),
  uniqueStudents: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  uniqueAssessments: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  totalSessions: z30.number().int(),
  totalLiveSessions: z30.number().int(),
  totalLiveCheckInSessions: z30.number().int(),
  totalProctoringHours: z30.number(),
  totalLiveHours: z30.number(),
  averageIntegrityScore: z30.number().nullish(),
  anomalyTypeCount: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  courseTableData: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstituteReportSummarySchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  startDate: z30.date(),
  endDate: z30.date(),
  summary: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstitutionReportEmailSchema = z30.object({
  id: z30.number().int(),
  institutionId: z30.number().int(),
  month: z30.number().int(),
  year: z30.number().int(),
  adminEmails: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  topMetrics: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  title: z30.string(),
  summary: z30.string(),
  isApproved: z30.boolean(),
  isSent: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ExemptedInstitutionReportEmailUsersSchema = z30.object({
  id: z30.number().int(),
  userId: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var SubAccountReportSchema = z30.object({
  id: z30.number().int(),
  subAccountId: z30.number().int(),
  instituteId: z30.number().int(),
  startDate: z30.date(),
  endDate: z30.date(),
  uniqueStudents: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  uniqueAssessments: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  totalSessions: z30.number().int(),
  totalLiveSessions: z30.number().int(),
  totalLiveCheckInSessions: z30.number().int(),
  totalProctoringHours: z30.number(),
  totalLiveHours: z30.number(),
  averageIntegrityScore: z30.number().nullish(),
  anomalyTypeCount: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  courseTableData: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var SubAccountReportSummarySchema = z30.object({
  id: z30.number().int(),
  subAccountId: z30.number().int(),
  startDate: z30.date(),
  endDate: z30.date(),
  summary: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var CourseReportSchema = z30.object({
  id: z30.number().int(),
  courseId: z30.number().int(),
  startDate: z30.date(),
  endDate: z30.date(),
  uniqueStudents: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  uniqueAssessments: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  totalSessions: z30.number().int(),
  totalLiveSessions: z30.number().int(),
  totalLiveCheckInSessions: z30.number().int(),
  totalProctoringHours: z30.number(),
  totalLiveHours: z30.number(),
  averageIntegrityScore: z30.number().nullish(),
  anomalyTypeCount: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  assessmentTableData: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var CourseReportSummarySchema = z30.object({
  id: z30.number().int(),
  courseId: z30.number().int(),
  startDate: z30.date(),
  endDate: z30.date(),
  summary: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var MoodleAccessTokenSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  accessToken: z30.string()
});
var SocketSessionSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionUuid: z30.string(),
  createdAt: z30.date()
});
var SocketEventSchema = z30.object({
  id: z30.number().int(),
  socketSessionId: z30.number().int(),
  name: z30.string(),
  createdAt: z30.date(),
  ackedAt: z30.date().nullish()
});
var LmsTokenAuthorizationRequestSchema = z30.object({
  id: z30.number().int(),
  state: z30.string(),
  userId: z30.number().int(),
  courseId: z30.number().int(),
  callbackUrl: z30.string(),
  createdAt: z30.date()
});
var ScratchNoteSchema = z30.object({
  id: z30.string(),
  proctoringSessionUuid: z30.string(),
  mimeType: ScratchNoteMimeTypeSchema,
  createdAt: z30.date(),
  uploadedAt: z30.date().nullish()
});
var ApiRouteConfigSchema = z30.object({
  id: z30.number().int(),
  version: ApiVersionSchema,
  route: z30.string(),
  instituteId: z30.number().int(),
  active: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RecordingSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  recordingId: z30.string(),
  sourceIndex: z30.number().int(),
  type: RecordingTypeSchema,
  chunked: z30.boolean().default(true),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RecordingConcatenationRequestSchema = z30.object({
  proctoringSessionId: z30.number().int(),
  type: z30.string(),
  number: z30.number().int(),
  requestId: z30.string(),
  status: ConcatenationRequestStatusSchema.default("POSTPROCESS_STARTED"),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RuleSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  scope: RuleScopeSchema,
  scopeValue: z30.string().nullish(),
  type: RuleTypeSchema,
  details: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  order: z30.number().int(),
  enabled: z30.boolean().default(true),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var TemporaryEnabledCourseSchema = z30.object({
  id: z30.number().int(),
  courseId: z30.string(),
  instituteId: z30.number().int()
});
var ProctoringSessionDataDumpSchema = z30.object({
  id: z30.string(),
  proctoringSessionId: z30.number().int(),
  source: z30.string(),
  mimeType: z30.string(),
  createdAt: z30.date(),
  uploadedAt: z30.date().nullish()
});
var InstitutionPaymentOptionsSchema = z30.object({
  id: z30.number().int(),
  currency: z30.string(),
  instituteId: z30.number().int(),
  automatedType: PaymentTypeSchema.default("assessment"),
  liveProctoringType: PaymentTypeSchema.default("assessment"),
  liveCheckInType: PaymentTypeSchema.default("assessment"),
  livePostReviewType: PaymentTypeSchema.default("assessment"),
  livePostReviewPrice: z30.number(),
  liveCheckInPrice: z30.number(),
  liveProctoringPrice: z30.number(),
  automatedProctoringPrice: z30.number(),
  enabled: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var StripePaymentSessionSchema = z30.object({
  id: z30.number().int(),
  assessmentId: z30.number().int(),
  userId: z30.number().int(),
  proctoringSessionId: z30.number().int().nullish(),
  stripeSessionId: z30.string(),
  paid: z30.boolean(),
  claimed: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RestrictedDevicePeerSchema2 = z30.object({
  id: z30.string(),
  proctoringSessionId: z30.number().int(),
  role: RestrictedDeviceRoleSchema2,
  platform: RestrictedDevicePlatformSchema2,
  options: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish()
});
var RestrictedDeviceStateSchema2 = z30.object({
  id: z30.number().int(),
  peerId: z30.string(),
  connected: z30.boolean(),
  data: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var RestrictedDeviceSingleUseGrantSchema = z30.object({
  id: z30.number().int(),
  peerId: z30.string(),
  secret: z30.string(),
  accountId: z30.string().nullish(),
  createdAt: z30.date()
});
var FeedbackSchema = z30.object({
  id: z30.number().int(),
  userId: z30.number().int(),
  satisfaction: SatisfactionLevelSchema,
  category: FeedbackCategorySchema,
  summary: z30.string(),
  details: z30.string(),
  status: FeedbackStatusSchema.default("notStarted"),
  isDeleted: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var EventBannerSchema = z30.object({
  id: z30.number().int(),
  createdBy: z30.number().int(),
  title: z30.string(),
  link: z30.string(),
  buttonText: z30.string(),
  institutions: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  roles: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  startDate: z30.date(),
  endDate: z30.date(),
  isDeleted: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InferenceTokenSchema = z30.object({
  id: z30.string(),
  proctoringSessionId: z30.number().int(),
  data: z30.string(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstituteStateSchema = z30.object({
  instituteId: z30.number().int(),
  lastVisualizerLaunchTime: z30.date().nullish(),
  lastLti1p3LaunchTime: z30.date().nullish(),
  ltiQuickLinkTemplate: z30.string().nullish(),
  ltiQuickLinkTemplateAt: z30.date().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var InstituteSetupGuideSchema = z30.object({
  instituteId: z30.number().int(),
  setupProfileId: z30.number().int().nullish(),
  setupQuizId: z30.number().int().nullish(),
  takeStepUserId: z30.number().int().nullish(),
  lti1p3SetupComplete: z30.boolean(),
  visualizerSetupComplete: z30.boolean(),
  apiSetupComplete: z30.boolean(),
  profileSetupComplete: z30.boolean(),
  extensionSetupComplete: z30.boolean(),
  createSetupComplete: z30.boolean(),
  takeSetupComplete: z30.boolean(),
  resultSetupComplete: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var ProctoringSessionSummarySchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  source: ProctoringSessionSummarySourceSchema.default("ai"),
  summary: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  authorId: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var SetupSupportChatMessageSchema = z30.object({
  id: z30.number().int(),
  proctoringSessionId: z30.number().int(),
  fromUser: z30.boolean(),
  timestamp: z30.date(),
  message: z30.string()
});
var CandidateInterviewOptionsSchema = z30.object({
  id: z30.number().int(),
  title: z30.string(),
  candidateName: z30.string(),
  candidateEmail: z30.string(),
  positionName: z30.string(),
  videoPlatform: VideoPlatformSchema,
  meetingLink: z30.string(),
  notes: z30.string().nullish(),
  proctoringAssessmentConfigurationId: z30.number().int()
});
var CronExpireRecordingSchema = z30.object({
  id: z30.number().int(),
  instituteId: z30.number().int(),
  lastProcessedId: z30.number().int(),
  startedAt: z30.date(),
  updatedAt: z30.date(),
  stoppedAt: z30.date().nullish()
});
var QuizQuestionSchema = z30.object({
  id: z30.number().int(),
  quizId: z30.number().int(),
  questionText: z30.string(),
  questionOrder: z30.number().int(),
  questionType: QuestionTypeSchema,
  correctionPoints: z30.number(),
  options: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  configuration: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  isDeleted: z30.boolean(),
  aiGradingEnabled: z30.boolean(),
  aiGradingCriteria: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var QuizResponseSchema = z30.object({
  id: z30.number().int(),
  sessionId: z30.number().int(),
  questionSnapshot: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10"),
  questionNo: z30.number().int(),
  answer: z30.unknown().refine((val) => {
    const getDepth = (obj, depth = 0) => {
      if (depth > 10)
        return depth;
      if (obj === null || typeof obj !== "object")
        return depth;
      const values = Object.values(obj);
      if (values.length === 0)
        return depth;
      return Math.max(...values.map((v) => getDepth(v, depth + 1)));
    };
    return getDepth(val) <= 10;
  }, "JSON nesting depth exceeds maximum of 10").nullish(),
  isAnswered: z30.boolean(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var QuizResponseGradeSchema = z30.object({
  id: z30.number().int(),
  responseId: z30.number().int(),
  score: z30.number(),
  notes: z30.string().nullish(),
  isDraft: z30.boolean().default(true),
  isGraded: z30.boolean().default(true),
  gradedByAi: z30.boolean(),
  aiJustification: z30.string().nullish(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});
var FingerprintsSchema = z30.object({
  id: z30.number().int(),
  fingerprint: z30.string(),
  questionId: z30.number().int(),
  responseId: z30.number().int(),
  wordOffset: z30.number().int(),
  createdAt: z30.date(),
  updatedAt: z30.date()
});

// src/contracts/admin/admin-schema.ts
import { z as z31 } from "zod";
var YujaAccountSchema2 = z31.object({
  yujaAccountId: z31.string(),
  name: z31.string(),
  salesforceId: z31.string()
});
var UpdateInstitutionInformationRequestSchema = z31.object({
  name: z31.nullish(z31.string()),
  origin: z31.nullish(z31.string()),
  subdomain: z31.nullish(z31.string()),
  lms: z31.nullish(z31.string()),
  product: z31.optional(InstitutionTypeSchema),
  accountType: z31.optional(AccountTypeSchema),
  yujaAccountId: z31.nullish(z31.string())
});
var AccessControlInstituteFieldsSchema = z31.object({
  name: z31.nullable(z31.string()),
  product: z31.string(),
  subdomain: z31.nullable(z31.string()),
  yujaAccountId: z31.nullable(z31.string()),
  origin: z31.nullable(z31.string()),
  platform: z31.nullable(z31.string())
});
var AccessControlInstituteSchema = AccessControlInstituteFieldsSchema.extend({
  id: z31.string()
});
var UpdateAccessControlInstituteRequestSchema = AccessControlInstituteFieldsSchema.partial();
var UsageLimitFieldsSchema = z31.object({
  usageMode: UsageModeSchema,
  category: UsageCategorySchema,
  proctorGroupId: z31.string(),
  type: UsageTypeSchema,
  resetableLimit: z31.number(),
  resetDate: z31.coerce.date(),
  oneTimeCredits: z31.number()
});
var GetUsageLimitQueryParamsSchema = z31.object({
  instituteUuid: z31.string(),
  /** @deprecated Use `instituteUuid` instead. Kept so old clients that still send `instituteId` keep working. */
  // eslint-disable-next-line deprecation/deprecation
  instituteId: z31.string().optional(),
  type: z31.optional(z31.array(UsageTypeSchema)),
  /** @deprecated Use `type` instead. Kept so old clients that still filter by `annualType` keep working. */
  // eslint-disable-next-line deprecation/deprecation
  annualType: z31.optional(z31.array(UsageTypeSchema)),
  category: z31.optional(z31.array(UsageCategorySchema)),
  proctorGroupId: z31.optional(z31.array(z31.string().nullable()))
});
var GetUsageLimitResponseSchema = z31.object({
  usage: z31.array(
    UsageLimitFieldsSchema.extend({
      /** @deprecated Use `type` instead. Kept for backward compatibility. */
      // eslint-disable-next-line deprecation/deprecation
      annualType: UsageTypeSchema,
      /** @deprecated Use `type` instead. Kept for backward compatibility. */
      // eslint-disable-next-line deprecation/deprecation
      oneTimeCreditsType: UsageTypeSchema
    })
  )
});
var UpdateUsageLimitQueryParamsSchema = z31.object({
  instituteId: z31.coerce.number()
});
var UpdateUsageLimitEntrySchema = UsageLimitFieldsSchema.extend({
  // Old callers always send `annualType`/`oneTimeCreditsType` and never `type`, so `type` can't be required.
  type: UsageTypeSchema.optional(),
  /** @deprecated Use `type` instead. Kept for legacy callers. */
  // eslint-disable-next-line deprecation/deprecation
  annualType: UsageTypeSchema.optional(),
  /** @deprecated Use `type` instead. Kept for legacy callers. */
  // eslint-disable-next-line deprecation/deprecation
  oneTimeCreditsType: UsageTypeSchema.optional()
}).refine(
  // eslint-disable-next-line deprecation/deprecation
  (limit) => limit.type !== void 0 || limit.annualType !== void 0 || limit.oneTimeCreditsType !== void 0,
  { message: "one of `type`, `annualType`, or `oneTimeCreditsType` is required" }
);
var UpdateUsageLimitRequestSchema = z31.array(UpdateUsageLimitEntrySchema).max(100, "too many usage limits in one request");
var GetInstituteUsageQueryParamsSchema = z31.object({
  instituteUuid: z31.string(),
  startTime: z31.coerce.date(),
  endTime: z31.optional(z31.coerce.date()),
  type: UsageTypeSchema,
  category: z31.optional(z31.array(UsageCategorySchema)),
  proctorGroupId: z31.optional(z31.array(z31.string().nullable()))
});
var GetInstituteUsageResponseSchema = z31.object({
  usage: z31.number()
});
var GetCurrentInstituteUsageQueryParamsSchema = z31.object({
  instituteUuid: z31.string()
});
var GetCurrentInstituteUsageResponseSchema = z31.object({
  usage: z31.array(
    z31.object({
      category: UsageCategorySchema,
      type: UsageTypeSchema,
      usage: z31.number()
    })
  )
});
var GetLiveUsageQueryParamsSchema = z31.object({ instituteId: z31.string() });
var GetLiveUsageResponseSchema = z31.object({ hours: z31.number() });

// src/contracts/admin/admin-http.ts
var GetInstitutionInformationContract = createContract({
  method: "GET",
  path: "/lti-institute/:instituteUuid/information",
  request: {
    pathParams: z32.object({
      instituteUuid: z32.string()
    })
  },
  response: {
    body: z32.object({
      name: z32.nullish(z32.string()),
      product: InstitutionTypeSchema,
      accountType: AccountTypeSchema,
      origin: z32.nullish(z32.string()),
      subdomain: z32.nullish(z32.string()),
      lms: z32.nullish(z32.string()),
      yujaAccountId: z32.nullish(z32.string())
    })
  }
});
var GetYuJaAccountsContract = createContract({
  method: "GET",
  path: "/lti-institute/yujaAccounts",
  response: {
    body: z32.array(YujaAccountSchema2)
  }
});
var UpdateInstitutionInformationContract = createContract({
  method: "PATCH",
  path: "/lti-institute/:instituteUuid/information",
  request: {
    pathParams: z32.object({
      instituteUuid: z32.string()
    }),
    body: UpdateInstitutionInformationRequestSchema
  }
});
var GetAllAccessControlInstitutesContract = createContract({
  method: "GET",
  path: "/institute",
  response: {
    body: createPaginatedResultsSchema(AccessControlInstituteSchema, "institutes")
  }
});
var GetInstituteUsageContract = createContract({
  method: "GET",
  path: "/usage",
  request: {
    queryParams: GetInstituteUsageQueryParamsSchema
  },
  response: {
    body: GetInstituteUsageResponseSchema
  }
});
var GetCurrentInstituteUsageContract = createContract({
  method: "GET",
  path: "/usage/current",
  request: {
    queryParams: GetCurrentInstituteUsageQueryParamsSchema
  },
  response: {
    body: GetCurrentInstituteUsageResponseSchema
  }
});
var GetUsageLimitsContract = createContract({
  method: "GET",
  path: "/usage/limit",
  request: {
    queryParams: GetUsageLimitQueryParamsSchema
  },
  response: {
    body: GetUsageLimitResponseSchema
  }
});
var UpdateUsageLimitsContract = createContract({
  method: "PUT",
  path: "/usage/limit/:instituteId",
  request: {
    pathParams: UpdateUsageLimitQueryParamsSchema,
    body: UpdateUsageLimitRequestSchema
  }
});
var GetLiveUsageContract = createContract({
  method: "GET",
  path: "/usage/live-proctoring",
  request: {
    queryParams: GetLiveUsageQueryParamsSchema
  },
  response: {
    body: GetLiveUsageResponseSchema
  }
});
var GetLiveCheckInUsageContract = createContract({
  method: "GET",
  path: "/usage/live-pop-in",
  request: {
    queryParams: GetLiveUsageQueryParamsSchema
  },
  response: {
    body: GetLiveUsageResponseSchema
  }
});

// src/contracts/institute/institute-schema.ts
import { z as z34 } from "zod";

// src/contracts/integration/integration-schema.ts
import { z as z33 } from "zod";
var LmsSetupGuideStepSchema = z33.enum(
  ["Create Assessment", "Take Assessment", "View Results", "Complete", "None"],
  {
    message: "Invalid setup guide step provided"
  }
);
var UpdateSetupGuideStepRequestSchema = z33.object({
  activeStep: LmsSetupGuideStepSchema
});
var InstituteSetupGuideFieldsSchema = z33.object({
  lti1p3SetupComplete: z33.boolean(),
  visualizerSetupComplete: z33.boolean(),
  apiSetupComplete: z33.boolean(),
  profileSetupComplete: z33.boolean(),
  extensionSetupComplete: z33.boolean(),
  createSetupComplete: z33.boolean(),
  takeSetupComplete: z33.boolean(),
  resultSetupComplete: z33.boolean(),
  setupProfileId: z33.nullable(z33.int()),
  setupQuizId: z33.nullable(z33.int())
});
var GetInstituteSetupGuideResponseSchema = InstituteSetupGuideFieldsSchema.extend({
  createQuizUrl: z33.optional(z33.string()),
  takeQuizUrl: z33.optional(z33.string()),
  resultQuizUrl: z33.optional(z33.string()),
  setupLmsQuizId: z33.optional(z33.string()),
  setupLmsCourseId: z33.optional(z33.string())
});
var UpdateInstituteSetupGuideStateRequestSchema = InstituteSetupGuideFieldsSchema.partial().extend({
  candidateQuizId: z33.optional(z33.int())
});
var legacySetupGuideQueryParamsSchema = z33.object({
  lmsCourseId: z33.optional(z33.string())
});
var UpdateInstituteSetupGuideQueryParamsSchema = legacySetupGuideQueryParamsSchema;
var GetInstituteSetupGuideQueryParamsSchema = legacySetupGuideQueryParamsSchema;

// src/contracts/institute/institute-schema.ts
var UpdateInstituteStateQueryParamsSchema = legacySetupGuideQueryParamsSchema;
var GetInstituteStateQueryParamsSchema = legacySetupGuideQueryParamsSchema;
var InstituteStateFieldsSchema = z34.object({
  lastVisualizerLaunchTime: z34.nullable(z34.string()),
  lastLti1p3LaunchTime: z34.nullable(z34.string())
});
var UpdateInstituteStateRequestSchema = InstituteStateFieldsSchema.partial();
var ApiUpdateInstituteStateRequestSchema = UpdateInstituteSetupGuideStateRequestSchema;
var GetInstituteStateResponseSchema = GetInstituteSetupGuideResponseSchema.extend({
  ...InstituteStateFieldsSchema.shape
});

// src/contracts/institute/institute-http.ts
var GetInstituteStateContract = createContract({
  method: "GET",
  path: "/institute-state",
  request: {
    // if new query params are added, can just extend this schema
    queryParams: GetInstituteStateQueryParamsSchema
  },
  response: {
    body: GetInstituteStateResponseSchema
  }
});
var UpdateInstituteStateContract = createContract({
  method: "PATCH",
  path: "/institute-state",
  request: {
    // if new query params are added, can just extend this schema
    queryParams: UpdateInstituteStateQueryParamsSchema,
    body: ApiUpdateInstituteStateRequestSchema
  },
  response: {
    body: GetInstituteStateResponseSchema
  }
});

// src/contracts/integration/integration-http.ts
var GetInstituteSetupGuideContract = createContract({
  method: "GET",
  path: "/institute-setup-guide",
  request: {
    queryParams: GetInstituteSetupGuideQueryParamsSchema
  },
  response: {
    body: GetInstituteSetupGuideResponseSchema
  }
});
var UpdateInstituteSetupGuideContract = createContract({
  method: "PATCH",
  path: "/institute-setup-guide",
  request: {
    queryParams: UpdateInstituteSetupGuideQueryParamsSchema,
    body: UpdateInstituteSetupGuideStateRequestSchema
  },
  response: {
    body: GetInstituteSetupGuideResponseSchema
  }
});
var GetSetupGuideStepContract = createContract({
  method: "GET",
  path: "/setup-guide-step",
  response: {
    body: LmsSetupGuideStepSchema
  }
});
var UpdateSetupGuideStepContract = createContract({
  method: "PATCH",
  path: "/setup-guide-step",
  request: {
    body: UpdateSetupGuideStepRequestSchema
  },
  response: {
    body: LmsSetupGuideStepSchema
  }
});

// src/contracts/proctoring-session-http.ts
import * as z35 from "zod/v4";
var GetProctoringSessionFlaggedKeywordsContract = createContract({
  method: "GET",
  path: "/proctoring-session/:proctoringSessionId/flagged-keywords",
  request: {
    pathParams: z35.object({
      proctoringSessionId: z35.string()
    })
  },
  response: {
    body: z35.array(z35.string())
  }
});

// src/contracts/schema/message.ts
import { z as z36 } from "zod";
var PingMessageSchema = z36.object({ topic: z36.string(), type: z36.literal("ping"), id: z36.string() });
var PongMessageSchema = z36.object({ topic: z36.string(), type: z36.literal("pong"), id: z36.string() });
var DisposeMessageSchema = z36.object({ topic: z36.string(), type: z36.literal("dispose") });
function createMessageSchema(type, messageType, PayloadSchema) {
  return z36.object({
    id: z36.string(),
    topic: z36.string(),
    type: z36.literal(type),
    messageType: z36.literal(messageType),
    payload: PayloadSchema
  });
}
function createMessageSchemaUnion(messageSchemas) {
  return z36.discriminatedUnion("messageType", messageSchemas);
}
function createConnectMessageSchema(schemas) {
  return createMessageSchemaUnion(
    Object.keys(schemas).map(
      (key) => z36.object({ id: z36.string(), topic: z36.string(), type: z36.literal("connect"), messageType: z36.literal(key) })
    )
  );
}
function createRequestMessageSchema(schemas) {
  return createMessageSchemaUnion(
    Object.entries(schemas).map(([key, { request }]) => createMessageSchema("request", key, request))
  );
}
function createResponseMessageSchema(schemas) {
  return createMessageSchemaUnion(
    Object.entries(schemas).map(([key, { response }]) => createMessageSchema("response", key, response))
  );
}
function createErrorMessageSchema(schemas) {
  return createMessageSchemaUnion(
    Object.keys(schemas).map(
      (key) => z36.object({
        id: z36.string(),
        topic: z36.string(),
        type: z36.literal("error"),
        messageType: z36.literal(key),
        payload: z36.unknown()
      })
    )
  );
}
var SerializedErrorSchema = z36.object({
  name: z36.string(),
  message: z36.string(),
  stack: z36.optional(z36.string()),
  errorClass: z36.optional(z36.string())
});
var MessageError = class extends Error {
  constructor({ name, message, stack, errorClass }) {
    super(message);
    this.name = name;
    this.stack = stack;
    this.errorClass = errorClass;
  }
};
function serializeError(error) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      errorClass: error.constructor?.name
    };
  } else if (error === void 0) {
    return void 0;
  } else {
    return JSON.parse(JSON.stringify(error));
  }
}
function deserializeError(serializedError) {
  const result = SerializedErrorSchema.safeParse(serializedError);
  if (result.success) {
    return new MessageError(result.data);
  }
  return serializedError;
}
function wrapMessageSchema(MessageSchema) {
  return z36.object({
    type: z36.literal("verity-handled-message"),
    message: MessageSchema
  });
}
function wrapMessage(message) {
  return { type: "verity-handled-message", message };
}
function isValidSource({ target, targetOrigin }, event) {
  if (targetOrigin !== "*" && event.origin !== targetOrigin) {
    return false;
  }
  if (!target) {
    return true;
  } else if (typeof target === "function") {
    const targetObj = target();
    if (targetObj) {
      return event.source === targetObj;
    } else {
      return false;
    }
  } else {
    return event.source === target;
  }
}
function createWindowMessageTransport({
  selfWindow = window,
  target,
  targetOrigin = "*"
}) {
  return {
    postMessage(message) {
      const targetObj = typeof target === "function" ? target() : target;
      targetObj?.postMessage(message, { targetOrigin });
    },
    subscribe(onMessage) {
      const dispose = () => selfWindow.removeEventListener("message", messageListener);
      const messageListener = (event) => {
        if (isValidSource({ target, targetOrigin }, event)) {
          onMessage(event.data);
        }
      };
      selfWindow.addEventListener("message", messageListener);
      return {
        dispose
      };
    },
    get context() {
      return `window:${selfWindow.location.href}`;
    }
  };
}
function createPortMessageTransport(port) {
  return {
    postMessage(message) {
      port.postMessage(message);
    },
    subscribe(onMessage) {
      const dispose = () => port.removeEventListener("message", messageListener);
      const messageListener = (event) => {
        onMessage(event.data);
      };
      port.addEventListener("message", messageListener);
      return {
        dispose
      };
    },
    get context() {
      return "port";
    }
  };
}
function createMessageSender({
  instanceName,
  transport,
  definition,
  messageOptions
}) {
  const { topic, schema } = definition;
  const logContext = `[MessageSender:${topic}:${instanceName}:${transport.context}]:`;
  const pendingMessages = {};
  const generateMessageId = () => crypto.randomUUID();
  const ResponseSchema = wrapMessageSchema(
    z36.discriminatedUnion("type", [
      createConnectMessageSchema(schema),
      createResponseMessageSchema(schema),
      createErrorMessageSchema(schema),
      PongMessageSchema
    ])
  );
  const subscription = transport.subscribe((message) => {
    const result = ResponseSchema.safeParse(message);
    if (result.success && result.data.message.topic === topic) {
      const response = result.data.message;
      if (response.type === "pong") {
        const pendingMessage2 = pendingMessages[response.id];
        if (pendingMessage2) {
          pendingMessage2.onResponse(void 0);
        }
        return;
      }
      const { id, type, messageType } = response;
      const pendingMessage = pendingMessages[id];
      if (pendingMessage) {
        if (pendingMessage.type === messageType) {
          if (type === "connect") {
            pendingMessage.onConnect();
          } else if (type === "error") {
            pendingMessage.onError(response.payload);
          } else {
            pendingMessage.onResponse(response.payload);
          }
        } else {
          console.error(
            `${logContext} Incorrect type for message ${id}; expected ${pendingMessage.type}, got ${messageType}`
          );
        }
      }
    }
  });
  let disposeInitialConnection;
  const connected = new Promise((resolve, reject) => {
    disposeInitialConnection = () => {
      clearInterval(pingInterval);
      clearTimeout(connectionTimeout);
    };
    const connectionTimeout = setTimeout(() => {
      reject(new Error(`${logContext} Initial connection timed out`));
      clearInterval(pingInterval);
    }, 3e4);
    const complete = () => {
      resolve();
      disposeInitialConnection();
    };
    const pingInterval = setInterval(() => {
      const id = generateMessageId();
      const disposeMessage = () => {
        clearTimeout(messageTimeout);
        delete pendingMessages[id];
      };
      const messageTimeout = setTimeout(disposeMessage, 1e3);
      pendingMessages[id] = {
        type: "ping",
        onConnect: () => {
          complete();
          disposeMessage();
        },
        onResponse: () => {
          complete();
          disposeMessage();
        },
        onError: (error) => {
          reject(new Error(`${logContext} ${error}`));
          disposeMessage();
        }
      };
      transport.postMessage(
        wrapMessage({
          type: "ping",
          topic,
          id
        })
      );
    }, 1e3);
  });
  return {
    ...Object.fromEntries(
      Object.keys(schema).map((key) => {
        const responseTimeoutDuration = messageOptions?.[key]?.responseTimeout;
        return [
          key,
          async (message) => {
            const id = generateMessageId();
            await connected;
            return await new Promise((resolve, reject) => {
              let responseTimeout;
              const disposeMessage = () => {
                clearTimeout(connectionTimeout);
                clearTimeout(responseTimeout);
                delete pendingMessages[id];
              };
              const connectionTimeout = setTimeout(() => {
                reject(new Error(`${logContext} Could not connect to message receiver for type ${key}, message ${id}`));
                disposeMessage();
              }, 1e3);
              if (responseTimeoutDuration !== null) {
                responseTimeout = setTimeout(() => {
                  reject(new Error(`${logContext} Message response timed out for type ${key}, message ${id}`));
                  disposeMessage();
                }, responseTimeoutDuration ?? 2e4);
              }
              pendingMessages[id] = {
                type: key,
                onConnect: () => clearTimeout(connectionTimeout),
                onResponse: (response) => {
                  resolve(response);
                  disposeMessage();
                },
                onError: (error) => {
                  const deserializedError = deserializeError(error);
                  if (deserializedError instanceof Error) {
                    reject(deserializedError);
                  } else {
                    reject(new Error(`${logContext} ${deserializedError}`));
                  }
                  disposeMessage();
                }
              };
              transport.postMessage(
                wrapMessage({
                  id,
                  type: "request",
                  topic,
                  messageType: key,
                  payload: message
                })
              );
            });
          }
        ];
      })
    ),
    dispose() {
      subscription.dispose();
      disposeInitialConnection();
      Object.values(pendingMessages).forEach((message) => message.onError("MessageSender disposed"));
      transport.postMessage(
        wrapMessage({
          topic,
          type: "dispose"
        })
      );
    }
  };
}
function createMessageConsumer({
  instanceName,
  transport,
  definition,
  handlers
}) {
  const { topic, schema } = definition;
  const logContext = `[MessageConsumer:${topic}:${instanceName}:${transport.context}]:`;
  const RequestSchema = wrapMessageSchema(
    z36.discriminatedUnion("type", [createRequestMessageSchema(schema), PingMessageSchema, DisposeMessageSchema])
  );
  const subscription = transport.subscribe(async (message) => {
    const parseResult = RequestSchema.safeParse(message);
    if (parseResult.success && parseResult.data.message.topic === topic) {
      const request = parseResult.data.message;
      if (request.type === "ping") {
        transport.postMessage(wrapMessage({ topic, type: "pong", id: request.id }));
        return;
      }
      if (request.type === "dispose") {
        subscription.dispose();
        return;
      }
      const { id, messageType, payload } = request;
      transport.postMessage(wrapMessage({ topic, id, type: "connect", messageType }));
      try {
        const result = await handlers[messageType](payload);
        transport.postMessage(
          wrapMessage({
            id,
            topic,
            type: "response",
            messageType,
            payload: result
          })
        );
      } catch (e) {
        console.error("%s", logContext, e);
        transport.postMessage(
          wrapMessage({
            id,
            topic,
            type: "error",
            messageType,
            payload: serializeError(e)
          })
        );
      }
    }
  });
  return subscription;
}
export {
  AssessmentUrlInfoSchema,
  AssessmentUrlInfoWithInstituteIdSchema,
  BrowserArchSchema,
  BrowserTypeSchema,
  ChromeOsAccessModeSchema,
  CreateInferenceTokenContract,
  DataDumpCallbackContract,
  DataDumpGrantContract,
  DataDumpLinksContract,
  DataDumpMimeTypeSchema,
  DataDumpSourceSchema,
  DevicePermissionUpdatedMessageSchema,
  DeviceTypeSchema,
  DevicesUpdatedMessageSchema,
  DisplayPermissionUpdatedMessageSchema,
  FaceDetectionEventSchema,
  FaceDetectionStateSchema,
  FlaggedKeywordEventSchema,
  FlaggedKeywordsSchema,
  GetAllAccessControlInstitutesContract,
  GetCurrentInstituteUsageContract,
  GetInstituteSetupGuideContract,
  GetInstituteStateContract,
  GetInstituteUsageContract,
  GetInstitutionInformationContract,
  GetLiveCheckInUsageContract,
  GetLiveUsageContract,
  GetProctoringSessionFlaggedKeywordsContract,
  GetSetupGuideStepContract,
  GetUsageLimitsContract,
  GetYuJaAccountsContract,
  LmsDetailsSchema,
  LmsSetupGuideStepSchema,
  LogMachineLearningQuizEventContract,
  MOBILE_MEDIA_DEVICE_ID,
  MachineLearningEventSchema,
  MediaStreamEndedMessageSchema,
  MediaStreamWebRtcMessageSchema,
  MessageError,
  ObjectDetectionEventSchema,
  ObjectDetectionStateSchema,
  PlatformInfoSchema,
  ProctoringComponentStateMessageSchema,
  ProctoringPlatformSchema,
  ProctoringSessionEventSchema,
  ProctoringSetupPageInfoSchema,
  ProctoringTaskIdSchema,
  ProctoringVerityOptionsSchema,
  RecordingModeSchema,
  RestrictedDeviceDeletePeerContract,
  RestrictedDeviceEventContract,
  RestrictedDeviceGrantContract,
  RestrictedDeviceLoggedEventRootSchema,
  RestrictedDeviceLoggedEventSchema,
  RestrictedDeviceOptionsSchema,
  RestrictedDevicePeerSchema,
  RestrictedDevicePlatformSchema,
  RestrictedDevicePublicStateSchema,
  RestrictedDeviceRegistrationContract,
  RestrictedDeviceRoleSchema,
  RestrictedDeviceStateDataSchema,
  RestrictedDeviceStateSchema,
  SelectUserMediaDeviceOptionsSchema,
  SerializableMediaDeviceInfoSchema,
  TargetObjectsSchema,
  UpdateInstituteSetupGuideContract,
  UpdateInstituteStateContract,
  UpdateInstitutionInformationContract,
  UpdateSetupGuideStepContract,
  UpdateUsageLimitsContract,
  UserMediaDevicePathSchema,
  coerceToContractAbsoluteBase,
  createMessageConsumer,
  createMessageSender,
  createPortMessageTransport,
  createWindowMessageTransport,
  deserializeError,
  getContractUrl,
  interceptorProctoringSetupPageMessagesDefinition,
  lmsOauthRedirectPageInterceptorMessagesDefinition,
  proctoringSetupPageInterceptorMessagesDefinition,
  proctoringSetupPagePlatformMessagesDefinition,
  serializeError,
  withContractBase
};
//# sourceMappingURL=index.js.map